import React, { useEffect, useRef, useState } from 'react';
import {
    Card,
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    InputNumber,
    Switch,
    Row,
    Col,
    Tag,
    Space,
    message,
    Tabs,
    Upload,
    Divider,
    Typography,
    Tooltip,
    Slider
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    StarOutlined,
    StarFilled,
    UploadOutlined,
    FileImageOutlined,
    PrinterOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import config from '../config';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CertificateTemplates = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isPreviewVisible, setIsPreviewVisible] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [signatureFile, setSignatureFile] = useState(null);
    const [backgroundFile, setBackgroundFile] = useState(null);
    const [canvaFile, setCanvaFile] = useState(null);
    const [watermarkFile, setWatermarkFile] = useState(null);
    const [removeLogo, setRemoveLogo] = useState(false);
    const [removeSignature, setRemoveSignature] = useState(false);
    const [removeBackground, setRemoveBackground] = useState(false);
    const [removeCanva, setRemoveCanva] = useState(false);
    const [removeWatermark, setRemoveWatermark] = useState(false);
    const [previewEditMode, setPreviewEditMode] = useState(false);
    const [previewOffsets, setPreviewOffsets] = useState({});
    const dragStateRef = useRef(null);
    const [livePreviewData, setLivePreviewData] = useState(null);
    const [signatureSlots, setSignatureSlots] = useState([{
        label: t('directorSignature'),
        position: 'center',
        showLabel: true
    }]);
    const [badges, setBadges] = useState([]);
    const [availablePlaceholders] = useState([
        '{{studentName}}',
        '{{courseName}}',
        '{{completionDate}}',
        '{{issueDate}}',
        '{{certificateNumber}}',
        '{{grade}}',
        '{{duration}}',
        '{{instructorName}}',
        '{{academyName}}'
    ]);
    const [placeholderStyles, setPlaceholderStyles] = useState({});

    const offsetFields = {
        logo: { x: 'logoOffsetX', y: 'logoOffsetY' },
        header: { x: 'headerOffsetX', y: 'headerOffsetY' },
        title: { x: 'titleOffsetX', y: 'titleOffsetY' },
        body: { x: 'bodyOffsetX', y: 'bodyOffsetY' },
        footer: { x: 'footerOffsetX', y: 'footerOffsetY' }
    };

    const getOffsetValue = (key, axis) => {
        // Check preview offsets first (used for dragging)
        if (previewOffsets?.[key]?.[axis] !== undefined) {
            return previewOffsets[key][axis];
        }

        // For badges, only use preview offsets (no form fields)
        if (key.startsWith('badge')) {
            return 0;
        }

        // For other elements (logo, header, etc.), check form fields
        const field = offsetFields[key]?.[axis];
        if (!field) return 0;

        const liveValue = livePreviewData?.[field];
        if (liveValue !== undefined) return liveValue;
        const formValue = form.getFieldValue(field);
        return formValue !== undefined ? formValue : 0;
    };

    const handleDragStart = (key, event) => {
        if (!previewEditMode) return;
        event.preventDefault();
        const startX = event.clientX;
        const startY = event.clientY;
        const startOffsetX = getOffsetValue(key, 'x');
        const startOffsetY = getOffsetValue(key, 'y');

        dragStateRef.current = {
            key,
            startX,
            startY,
            startOffsetX,
            startOffsetY
        };
    };

    useEffect(() => {
        const handleMouseMove = (event) => {
            if (!dragStateRef.current) return;
            const { key, startX, startY, startOffsetX, startOffsetY } = dragStateRef.current;
            const deltaX = event.clientX - startX;
            const deltaY = event.clientY - startY;

            setPreviewOffsets((prev) => ({
                ...prev,
                [key]: {
                    x: startOffsetX + deltaX,
                    y: startOffsetY + deltaY
                }
            }));
        };

        const handleMouseUp = () => {
            dragStateRef.current = null;
        };

        if (previewEditMode) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [previewEditMode]);

    // Fetch templates
    const { data: templatesData, isLoading } = useQuery('certificateTemplates', async () => {
        const response = await api.get('/certificate-templates');
        return response.data.data;
    });

    // Create template mutation
    const createMutation = useMutation(
        async (values) => {
            const response = await api.post('/certificate-templates', values);
            return response.data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('certificateTemplates');
                message.success(t('templateCreatedSuccessfully'));
                setIsModalVisible(false);
                form.resetFields();
                setLogoFile(null);
                setSignatureFile(null);
                setPreviewOffsets({});
                setBadges([]);
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('errorCreatingTemplate'));
            }
        }
    );

    // Update template mutation
    const updateMutation = useMutation(
        async ({ id, values }) => {
            const response = await api.put(`/certificate-templates/${id}`, values);
            return response.data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('certificateTemplates');
                message.success(t('templateUpdatedSuccessfully'));
                setIsModalVisible(false);
                setEditingTemplate(null);
                form.resetFields();
                setLogoFile(null);
                setSignatureFile(null);
                setBackgroundFile(null);
                setPreviewOffsets({});
                setBadges([]);
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('errorUpdatingTemplate'));
            }
        }
    );

    // Delete template mutation
    const deleteMutation = useMutation(
        async (id) => {
            const response = await api.delete(`/certificate-templates/${id}`);
            return response.data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('certificateTemplates');
                message.success(t('templateDeletedSuccessfully'));
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('errorDeletingTemplate'));
            }
        }
    );

    // Set default mutation
    const setDefaultMutation = useMutation(
        async (id) => {
            const response = await api.put(`/certificate-templates/${id}/set-default`);
            return response.data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('certificateTemplates');
                message.success(t('defaultTemplateSet'));
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('errorSettingDefault'));
            }
        }
    );

    // Upload logo
    const uploadLogo = async (file) => {
        const formData = new FormData();
        formData.append('logo', file);
        const response = await api.post('/certificate-templates/upload-logo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data.data.path;
    };

    // Upload signature
    const uploadSignature = async (file) => {
        const formData = new FormData();
        formData.append('signature', file);
        const response = await api.post('/certificate-templates/upload-signature', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data.data.path;
    };

    // Upload background
    const uploadBackground = async (file) => {
        const formData = new FormData();
        formData.append('background', file);
        const response = await api.post('/certificate-templates/upload-background', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data.data.path;
    };

    // Upload Canva file
    const uploadCanva = async (file) => {
        const formData = new FormData();
        formData.append('canva', file);
        const response = await api.post('/certificate-templates/upload-canva', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data.data;
    };

    // Upload watermark
    const uploadWatermark = async (file) => {
        const formData = new FormData();
        formData.append('watermark', file);
        const response = await api.post('/certificate-templates/upload-watermark', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data.data;
    };

    const handleCreate = () => {
        setEditingTemplate(null);
        form.resetFields();
        setLogoFile(null);
        setSignatureFile(null);
        setBackgroundFile(null);
        setCanvaFile(null);
        setWatermarkFile(null);
        setRemoveLogo(false);
        setRemoveSignature(false);
        setRemoveBackground(false);
        setRemoveCanva(false);
        setRemoveWatermark(false);
        setSignatureSlots([{
            label: t('directorSignature'),
            position: 'center',
            showLabel: true
        }]);
        setBadges([]);
        setPlaceholderStyles({});
        setLivePreviewData(null);
        setIsModalVisible(true);
    };

    const handleEdit = (template) => {
        setEditingTemplate(template);
        const editValues = {
            name: template.name,
            isDefault: template.isDefault,
            layout: template.design?.layout,
            orientation: template.design?.orientation,
            backgroundColor: template.design?.backgroundColor,
            backgroundImage: template.design?.backgroundImage,
            borderStyle: template.design?.borderStyle,
            borderColor: template.design?.borderColor,
            secondaryBorderColor: template.design?.secondaryBorderColor,
            borderPattern: template.design?.borderPattern,
            borderWidth: template.design?.borderWidth,
            fullFrameEnabled: template.design?.frameDecoration?.enabled,
            frameStyle: template.design?.frameDecoration?.style,
            frameColor: template.design?.frameDecoration?.color,
            frameThickness: template.design?.frameDecoration?.thickness,
            cornerDecorationEnabled: template.design?.cornerDecoration?.enabled,
            cornerDecorationType: template.design?.cornerDecoration?.type,
            cornerDecorationSize: template.design?.cornerDecoration?.size,
            cornerDecorationColor: template.design?.cornerDecoration?.color,
            contentPadding: template.design?.layoutSettings?.contentPadding,
            headerAlign: template.design?.layoutSettings?.headerAlign,
            titleAlign: template.design?.layoutSettings?.titleAlign,
            bodyAlign: template.design?.layoutSettings?.bodyAlign,
            footerAlign: template.design?.layoutSettings?.footerAlign,
            headerSpacing: template.design?.layoutSettings?.headerSpacing,
            titleSpacing: template.design?.layoutSettings?.titleSpacing,
            bodySpacing: template.design?.layoutSettings?.bodySpacing,
            footerSpacing: template.design?.layoutSettings?.footerSpacing,
            bodyMaxWidth: template.design?.layoutSettings?.bodyMaxWidth,
            footerMaxWidth: template.design?.layoutSettings?.footerMaxWidth,
            logoAlign: template.design?.layoutSettings?.logoAlign,
            logoMaxWidth: template.design?.layoutSettings?.logoMaxWidth,
            logoMaxHeight: template.design?.layoutSettings?.logoMaxHeight,
            logoOffsetTop: template.design?.layoutSettings?.logoOffsetTop,
            logoOffsetX: template.design?.layoutSettings?.logoOffsetX,
            logoOffsetY: template.design?.layoutSettings?.logoOffsetY,
            headerOffsetX: template.design?.layoutSettings?.headerOffsetX,
            headerOffsetY: template.design?.layoutSettings?.headerOffsetY,
            titleOffsetX: template.design?.layoutSettings?.titleOffsetX,
            titleOffsetY: template.design?.layoutSettings?.titleOffsetY,
            bodyOffsetX: template.design?.layoutSettings?.bodyOffsetX,
            bodyOffsetY: template.design?.layoutSettings?.bodyOffsetY,
            footerOffsetX: template.design?.layoutSettings?.footerOffsetX,
            footerOffsetY: template.design?.layoutSettings?.footerOffsetY,
            instituteName: template.header?.instituteName,
            instituteNameFontFamily: template.header?.instituteNameFont?.family,
            instituteNameFontSize: template.header?.instituteNameFont?.size,
            instituteNameFontColor: template.header?.instituteNameFont?.color,
            titleText: template.body?.title?.text,
            titleFontFamily: template.body?.title?.font?.family,
            titleFontSize: template.body?.title?.font?.size,
            titleFontColor: template.body?.title?.font?.color,
            bodyTemplate: template.body?.text?.template,
            bodyFontFamily: template.body?.text?.font?.family,
            bodyFontSize: template.body?.text?.font?.size,
            bodyFontWeight: template.body?.text?.font?.weight || 'normal',
            bodyFontColor: template.body?.text?.font?.color,
            showDate: template.footer?.showDate,
            showSignature: template.footer?.showSignature,
            signatureLabel: template.footer?.signatureLabel,
            issueDate: template.footer?.issueDate,
            watermarkOpacity: template.watermark?.opacity || 0.1,
            watermarkSize: template.watermark?.size || 300,
        };
        form.setFieldsValue(editValues);
        setLivePreviewData(editValues);
        setRemoveLogo(false);
        setRemoveSignature(false);
        setRemoveBackground(false);
        setRemoveCanva(false);
        setRemoveWatermark(false);
        // Load signature slots if available
        if (template.signatureSlots && template.signatureSlots.length > 0) {
            setSignatureSlots(template.signatureSlots);
        }
        // Load badges if available
        if (template.badges && template.badges.length > 0) {
            setBadges(template.badges);
        }
        // Load placeholder styles if available
        if (template.placeholderStyles) {
            setPlaceholderStyles(template.placeholderStyles);
        }
        setLivePreviewData(template);
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: t('confirmDelete'),
            content: t('confirmDeleteTemplate'),
            okText: t('delete'),
            okType: 'danger',
            cancelText: t('cancel'),
            onOk: () => deleteMutation.mutate(id)
        });
    };

    const handleSetDefault = (id) => {
        setDefaultMutation.mutate(id);
    };

    const handlePreview = (template) => {
        console.log('Opening preview for template:', {
            name: template.name,
            hasBadges: !!template.badges,
            badgesCount: template.badges?.length || 0,
            badges: template.badges
        });
        setPreviewTemplate(template);
        setIsPreviewVisible(true);
    };

    const handlePrint = (template) => {
        const printWindow = window.open('', '_blank');
        const isLandscape = template.design?.orientation === 'landscape';

        const sampleData = {
            '{{studentName}}': 'John Doe',
            '{{courseName}}': 'Sample Course',
            '{{completionDate}}': new Date().toLocaleDateString(),
            '{{issueDate}}': new Date().toLocaleDateString(),
            '{{certificateNumber}}': 'CERT-001',
            '{{grade}}': 'A+',
            '{{duration}}': '3 months',
            '{{instructorName}}': 'Jane Smith',
            '{{academyName}}': 'Academy Name'
        };

        const processBodyText = (bodyTemplate, placeholders) => {
            if (!bodyTemplate) return '';
            const parts = bodyTemplate.split(/(\{\{[^}]+\}\})/g);
            return parts.map(part => {
                if (part.match(/\{\{[^}]+\}\}/)) {
                    const style = placeholders[part] || {};
                    const value = sampleData[part] || part;
                    return `<span style="font-family: ${style.fontFamily || 'inherit'}; font-size: ${style.fontSize ? style.fontSize + 'px' : 'inherit'}; color: ${style.color || 'inherit'}; font-weight: ${style.fontWeight || 'normal'}; font-style: ${style.fontStyle || 'normal'}; text-decoration: ${style.textDecoration || 'none'}; text-transform: ${style.textTransform || 'none'};">${value}</span>`;
                }
                return part;
            }).join('');
        };

        const borderStyle = template.design?.borderStyle || 'single';
        const borderColor = template.design?.borderColor || '#000000';
        const secondaryColor = template.design?.secondaryBorderColor || '#DAA520';

        let boxShadow = 'none';
        let borderCSS = 'none';
        if (borderStyle === 'none') boxShadow = 'none';
        else if (borderStyle === 'single') boxShadow = `inset 0 0 0 2px ${borderColor}`;
        else if (borderStyle === 'double') boxShadow = `inset 0 0 0 2px ${borderColor}, inset 0 0 0 6px ${secondaryColor}, inset 0 0 0 8px ${borderColor}`;
        else if (borderStyle === 'triple') boxShadow = `inset 0 0 0 2px ${borderColor}, inset 0 0 0 5px ${secondaryColor}, inset 0 0 0 7px ${borderColor}, inset 0 0 0 10px ${secondaryColor}, inset 0 0 0 12px ${borderColor}`;
        else if (borderStyle === 'ornate') boxShadow = `inset 0 0 0 1px ${borderColor}, inset 0 0 0 3px ${secondaryColor}, inset 0 0 0 4px ${borderColor}, inset 0 0 0 8px ${secondaryColor}, inset 0 0 0 10px ${borderColor}`;
        else if (borderStyle === 'dashed') borderCSS = `3px dashed ${borderColor}`;

        const backgroundImage = template.canvaImport?.enabled && template.canvaImport?.originalFile
            ? `url('${config.baseUrl}${template.canvaImport.originalFile}')`
            : template.design?.backgroundImage
                ? `url('${config.baseUrl}${template.design.backgroundImage}')`
                : 'none';

        // Frame decorations
        const frameEnabled = template.design?.frameDecoration?.enabled;
        const frameStyle = template.design?.frameDecoration?.style || 'vintage_border';
        const frameColor = template.design?.frameDecoration?.color || '#8B4513';
        const frameThickness = template.design?.frameDecoration?.thickness || 2;

        let frameSVG = '';
        if (frameEnabled) {
            if (frameStyle === 'vintage_border') {
                frameSVG = `
                    <svg style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 5;" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <rect x="1" y="1" width="98" height="98" fill="none" stroke="${frameColor}" stroke-width="${frameThickness * 0.15}" vector-effect="non-scaling-stroke" />
                        <rect x="3.5" y="3.5" width="93" height="93" fill="none" stroke="${frameColor}" stroke-width="${frameThickness * 0.08}" vector-effect="non-scaling-stroke" />
                        <rect x="6" y="6" width="88" height="88" fill="none" stroke="${frameColor}" stroke-width="${frameThickness * 0.05}" vector-effect="non-scaling-stroke" />
                        <path d="M7,7 Q7,12 10,12 Q12,12 12,10 Q12,7 7,7 M7,7 L12,12 M10,7 Q10,10 7,10" stroke="${frameColor}" fill="none" stroke-width="${frameThickness * 0.06}" vector-effect="non-scaling-stroke" />
                        <path d="M93,7 Q93,12 90,12 Q88,12 88,10 Q88,7 93,7 M93,7 L88,12 M90,7 Q90,10 93,10" stroke="${frameColor}" fill="none" stroke-width="${frameThickness * 0.06}" vector-effect="non-scaling-stroke" />
                        <path d="M7,93 Q7,88 10,88 Q12,88 12,90 Q12,93 7,93 M7,93 L12,88 M10,93 Q10,90 7,90" stroke="${frameColor}" fill="none" stroke-width="${frameThickness * 0.06}" vector-effect="non-scaling-stroke" />
                        <path d="M93,93 Q93,88 90,88 Q88,88 88,90 Q88,93 93,93 M93,93 L88,88 M90,93 Q90,90 93,90" stroke="${frameColor}" fill="none" stroke-width="${frameThickness * 0.06}" vector-effect="non-scaling-stroke" />
                        <circle cx="50" cy="4" r="1.5" fill="${frameColor}" />
                        <circle cx="50" cy="96" r="1.5" fill="${frameColor}" />
                        <circle cx="4" cy="50" r="1.5" fill="${frameColor}" />
                        <circle cx="96" cy="50" r="1.5" fill="${frameColor}" />
                    </svg>
                `;
            } else if (frameStyle === 'elegant_frame') {
                frameSVG = `
                    <svg style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 5;" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <rect x="2" y="2" width="96" height="96" fill="none" stroke="${frameColor}" stroke-width="${frameThickness * 0.12}" vector-effect="non-scaling-stroke" />
                        <path d="M8,5 Q10,3 12,5 Q14,3 16,5 Q18,3 20,5 Q22,3 24,5 Q26,3 28,5 Q30,3 32,5 Q34,3 36,5 Q38,3 40,5 Q42,3 44,5 Q46,3 48,5 Q50,3 52,5 Q54,3 56,5 Q58,3 60,5 Q62,3 64,5 Q66,3 68,5 Q70,3 72,5 Q74,3 76,5 Q78,3 80,5 Q82,3 84,5 Q86,3 88,5 Q90,3 92,5" stroke="${frameColor}" fill="none" stroke-width="${frameThickness * 0.06}" vector-effect="non-scaling-stroke" />
                        <path d="M8,95 Q10,97 12,95 Q14,97 16,95 Q18,97 20,95 Q22,97 24,95 Q26,97 28,95 Q30,97 32,95 Q34,97 36,95 Q38,97 40,95 Q42,97 44,95 Q46,97 48,95 Q50,97 52,95 Q54,97 56,95 Q58,97 60,95 Q62,97 64,95 Q66,97 68,95 Q70,97 72,95 Q74,97 76,95 Q78,97 80,95 Q82,97 84,95 Q86,97 88,95 Q90,97 92,95" stroke="${frameColor}" fill="none" stroke-width="${frameThickness * 0.06}" vector-effect="non-scaling-stroke" />
                        <circle cx="8" cy="8" r="2" fill="none" stroke="${frameColor}" stroke-width="${frameThickness * 0.05}" />
                        <circle cx="92" cy="8" r="2" fill="none" stroke="${frameColor}" stroke-width="${frameThickness * 0.05}" />
                        <circle cx="8" cy="92" r="2" fill="none" stroke="${frameColor}" stroke-width="${frameThickness * 0.05}" />
                        <circle cx="92" cy="92" r="2" fill="none" stroke="${frameColor}" stroke-width="${frameThickness * 0.05}" />
                    </svg>
                `;
            } else if (frameStyle === 'ornate_full') {
                frameSVG = `
                    <svg style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 5;" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <rect x="1" y="1" width="98" height="98" fill="none" stroke="${frameColor}" stroke-width="${frameThickness * 0.15}" vector-effect="non-scaling-stroke" />
                        <rect x="3" y="3" width="94" height="94" fill="none" stroke="${frameColor}" stroke-width="${frameThickness * 0.10}" vector-effect="non-scaling-stroke" />
                        <rect x="5.5" y="5.5" width="89" height="89" fill="none" stroke="${frameColor}" stroke-width="${frameThickness * 0.06}" vector-effect="non-scaling-stroke" />
                        <rect x="8" y="8" width="84" height="84" fill="none" stroke="${frameColor}" stroke-width="${frameThickness * 0.04}" vector-effect="non-scaling-stroke" />
                        <path d="M10,10 L15,10 L15,15 L10,15 Z M12.5,10 L12.5,15 M10,12.5 L15,12.5" stroke="${frameColor}" stroke-width="${frameThickness * 0.05}" vector-effect="non-scaling-stroke" />
                        <path d="M90,10 L85,10 L85,15 L90,15 Z M87.5,10 L87.5,15 M85,12.5 L90,12.5" stroke="${frameColor}" stroke-width="${frameThickness * 0.05}" vector-effect="non-scaling-stroke" />
                        <path d="M10,90 L15,90 L15,85 L10,85 Z M12.5,85 L12.5,90 M10,87.5 L15,87.5" stroke="${frameColor}" stroke-width="${frameThickness * 0.05}" vector-effect="non-scaling-stroke" />
                        <path d="M90,90 L85,90 L85,85 L90,85 Z M87.5,85 L87.5,90 M85,87.5 L90,87.5" stroke="${frameColor}" stroke-width="${frameThickness * 0.05}" vector-effect="non-scaling-stroke" />
                    </svg>
                `;
            } else if (frameStyle === 'classical') {
                frameSVG = `
                    <svg style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 5;" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <rect x="2" y="2" width="96" height="96" fill="none" stroke="${frameColor}" stroke-width="${frameThickness * 0.15}" vector-effect="non-scaling-stroke" />
                        <rect x="5" y="5" width="90" height="90" fill="none" stroke="${frameColor}" stroke-width="${frameThickness * 0.08}" vector-effect="non-scaling-stroke" />
                        <line x1="8" y1="8" x2="15" y2="8" stroke="${frameColor}" stroke-width="${frameThickness * 0.08}" />
                        <line x1="8" y1="8" x2="8" y2="15" stroke="${frameColor}" stroke-width="${frameThickness * 0.08}" />
                        <line x1="92" y1="8" x2="85" y2="8" stroke="${frameColor}" stroke-width="${frameThickness * 0.08}" />
                        <line x1="92" y1="8" x2="92" y2="15" stroke="${frameColor}" stroke-width="${frameThickness * 0.08}" />
                        <line x1="8" y1="92" x2="15" y2="92" stroke="${frameColor}" stroke-width="${frameThickness * 0.08}" />
                        <line x1="8" y1="92" x2="8" y2="85" stroke="${frameColor}" stroke-width="${frameThickness * 0.08}" />
                        <line x1="92" y1="92" x2="85" y2="92" stroke="${frameColor}" stroke-width="${frameThickness * 0.08}" />
                        <line x1="92" y1="92" x2="92" y2="85" stroke="${frameColor}" stroke-width="${frameThickness * 0.08}" />
                    </svg>
                `;
            }
        }

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Print Certificate Template - ${template.name}</title>
                <style>
                    @page {
                        size: ${isLandscape ? '297mm 210mm' : '210mm 297mm'};
                        margin: 0;
                    }
                    @media print {
                        html, body {
                            width: ${isLandscape ? '297mm' : '210mm'};
                            height: ${isLandscape ? '210mm' : '297mm'};
                        }
                        .certificate {
                            width: ${isLandscape ? '297mm' : '210mm'} !important;
                            height: ${isLandscape ? '210mm' : '297mm'} !important;
                        }
                    }
                    * {
                        box-sizing: border-box;
                    }
                    body {
                        margin: 0;
                        padding: 0;
                        width: 100%;
                        height: 100vh;
                    }
                    .certificate {
                        width: ${isLandscape ? '297mm' : '210mm'};
                        height: ${isLandscape ? '210mm' : '297mm'};
                        background-color: ${template.design?.backgroundColor || '#FFFFFF'};
                        background-image: ${backgroundImage};
                        background-size: cover;
                        background-position: center;
                        background-repeat: no-repeat;
                        padding: ${template.design?.layoutSettings?.contentPadding || 30}px;
                        position: relative;
                        text-align: left;
                        box-shadow: ${boxShadow};
                        border: ${borderCSS};
                        page-break-after: always;
                        overflow: hidden;
                    }
                    .logo {
                        position: absolute;
                        top: 15mm;
                        ${template.header?.logoPosition === 'center' ? 'left: 50%; transform: translateX(-50%);' : template.header?.logoPosition === 'right' ? 'right: 15mm;' : 'left: 15mm;'}
                        max-width: ${template.header?.logoSize || 80}px;
                        max-height: ${template.header?.logoSize || 80}px;
                        object-fit: contain;
                    }
                    .header {
                        font-family: ${template.header?.fontFamily || 'Arial'};
                        font-size: ${template.header?.fontSize || 20}px;
                        color: ${template.header?.fontColor || '#000000'};
                        text-align: ${template.header?.align || 'center'};
                        margin-bottom: ${template.header?.spacing || 15}px;
                        transform: translate(${template.design?.layoutSettings?.headerOffsetX || 0}px, ${template.design?.layoutSettings?.headerOffsetY || 0}px);
                    }
                    .title {
                        font-family: ${template.title?.fontFamily || 'Arial'};
                        font-size: ${template.title?.fontSize || 36}px;
                        color: ${template.title?.fontColor || '#000000'};
                        text-align: ${template.title?.align || 'center'};
                        margin-bottom: ${template.title?.spacing || 20}px;
                        transform: translate(${template.design?.layoutSettings?.titleOffsetX || 0}px, ${template.design?.layoutSettings?.titleOffsetY || 0}px);
                    }
                    .body {
                        font-family: ${template.body?.text?.fontFamily || 'Arial'};
                        font-size: ${template.body?.text?.fontSize || 16}px;
                        font-weight: ${template.body?.text?.fontWeight || 'normal'};
                        color: ${template.body?.text?.fontColor || '#333333'};
                        text-align: ${template.body?.text?.align || 'center'};
                        max-width: ${template.body?.text?.maxWidth || 600}px;
                        margin: 0 auto ${template.body?.text?.spacing || 30}px;
                        white-space: pre-wrap;
                        transform: translate(${template.design?.layoutSettings?.bodyOffsetX || 0}px, ${template.design?.layoutSettings?.bodyOffsetY || 0}px);
                    }
                    .badge {
                        position: absolute;
                        z-index: 10;
                    }
                    .watermark {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        z-index: 1;
                        pointer-events: none;
                    }
                    .footer-container {
                        position: absolute;
                        bottom: 15mm;
                        left: 15mm;
                        right: 15mm;
                        transform: translate(${template.design?.layoutSettings?.footerOffsetX || 0}px, ${template.design?.layoutSettings?.footerOffsetY || 0}px);
                    }
                    .signatures {
                        display: flex;
                        justify-content: space-around;
                        margin-bottom: 30px;
                    }
                    .signature-slot {
                        text-align: center;
                        max-width: 200px;
                        ${template.signatureSlots?.length === 1 ? 'flex: 0 0 auto;' : 'flex: 1;'}
                    }
                    .signature-line {
                        border-top: 1px solid #000;
                        padding-top: 5px;
                        font-size: 14px;
                    }
                    .date-footer {
                        text-align: center;
                        font-size: 14px;
                        margin-bottom: 5px;
                    }
                </style>
            </head>
            <body>
                <div class="certificate">
                    ${frameSVG}
                    
                    ${template.header?.logo ? `<img src="${config.baseUrl}${template.header.logo}" class="logo" alt="Logo" />` : ''}
                    
                    ${template.header?.instituteName ? `<h1 class="header">${template.header.instituteName}</h1>` : ''}
                    
                    <h2 class="title">${template.title?.text || 'Certificate of Completion'}</h2>
                    
                    <div class="body">${processBodyText(template.body?.text?.template || 'This is to certify that {{studentName}} has successfully completed the {{courseName}} course.', template.placeholderStyles || {})}</div>
                    
                    ${template.badges?.map((badge, index) => `
                        <div class="badge" style="left: ${badge.x}%; top: ${badge.y}%; transform: translate(-50%, -50%);">
                            <img src="${config.baseUrl}${badge.url}" alt="Badge ${index + 1}" style="width: ${badge.size}px; height: ${badge.size}px; object-fit: contain;" />
                        </div>
                    `).join('') || ''}
                    
                    ${template.watermark?.image ? `
                        <div class="watermark" style="opacity: ${template.watermark.opacity || 0.1};">
                            <img src="${config.baseUrl}${template.watermark.image}" alt="Watermark" style="width: ${template.watermark.size || 300}px; height: auto; object-fit: contain;" />
                        </div>
                    ` : ''}
                    
                    <div class="footer-container">
                        ${template.signatureSlots?.length > 0 ? `
                            <div class="signatures">
                                ${template.signatureSlots.map(slot => `
                                    <div class="signature-slot">
                                        ${slot.showSignerName && slot.signerName ? `<div class="signer-name" style="font-weight: bold; margin-bottom: 3px;">${slot.signerName}</div>` : ''}
                                        <div class="signature-line">${slot.showLabel ? slot.label || 'Signature' : ''}</div>
                                    </div>
                                `).join('')}
                            </div>
                        ` : ''}
                        ${template.footer?.showDate ? `<div class="date-footer">${template.footer.issueDate || new Date().toLocaleDateString()}</div>` : ''}
                    </div>
                </div>
                <script>
                    window.onload = function() {
                        setTimeout(function() {
                            window.print();
                        }, 500);
                    };
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleSubmit = async (values) => {
        try {
            let logoPath = editingTemplate?.header?.logo;
            let signaturePath = editingTemplate?.footer?.signatureImage;
            let backgroundPath = editingTemplate?.design?.backgroundImage;
            let canvaData = editingTemplate?.canvaImport;

            // Upload logo if new file selected
            if (logoFile) {
                logoPath = await uploadLogo(logoFile);
            }

            if (removeLogo && !logoFile) {
                logoPath = null;
            }

            // Upload signature if new file selected
            if (signatureFile) {
                signaturePath = await uploadSignature(signatureFile);
            }

            if (removeSignature && !signatureFile) {
                signaturePath = null;
            }

            // Upload background if new file selected
            if (backgroundFile) {
                backgroundPath = await uploadBackground(backgroundFile);
            }

            if (removeBackground && !backgroundFile) {
                backgroundPath = null;
            }

            // Upload Canva file if selected
            if (canvaFile) {
                const uploadedCanva = await uploadCanva(canvaFile);
                canvaData = {
                    enabled: true,
                    originalFile: uploadedCanva.path,
                    fileType: uploadedCanva.fileType,
                    importDate: new Date()
                };
                // Use Canva file as background if no background specified
                if (!backgroundPath) {
                    backgroundPath = uploadedCanva.path;
                }
            }

            // Upload badges
            const uploadedBadges = await Promise.all(
                badges
                    .filter(badge => {
                        const hasFileOrUrl = badge.file || badge.url;
                        if (!hasFileOrUrl) {
                            console.log('Skipping badge without file or url:', badge);
                        }
                        return hasFileOrUrl;
                    })
                    .map(async (badge, index) => {
                        const badgeKey = `badge${index}`;
                        const offsetX = previewOffsets[badgeKey]?.x || 0;
                        const offsetY = previewOffsets[badgeKey]?.y || 0;

                        // Convert pixel offsets to percentage
                        // Container is 210mm (portrait) or 297mm (landscape) - approximately 793px or 1122px at 96dpi
                        const orientation = values.orientation || 'portrait';
                        const containerWidth = orientation === 'landscape' ? 1122 : 793;
                        const containerHeight = orientation === 'landscape' ? 793 : 1122;

                        const percentOffsetX = (offsetX / containerWidth) * 100;
                        const percentOffsetY = (offsetY / containerHeight) * 100;

                        if (badge.file) {
                            try {
                                console.log('Uploading new badge file:', {
                                    fileName: badge.file.name,
                                    fileSize: badge.file.size,
                                    fileType: badge.file.type
                                });
                                const formData = new FormData();
                                formData.append('badge', badge.file);

                                // Log FormData contents
                                for (let pair of formData.entries()) {
                                    console.log('FormData entry:', pair[0], pair[1]);
                                }

                                const response = await api.post('/certificate-templates/upload-badge', formData, {
                                    headers: {
                                        'Content-Type': 'multipart/form-data'
                                    }
                                });
                                console.log('Badge upload successful:', response.data);
                                return {
                                    url: response.data.data.path,
                                    x: (badge.x || 50) + percentOffsetX,
                                    y: (badge.y || 50) + percentOffsetY,
                                    size: badge.size || 80
                                };
                            } catch (error) {
                                console.error('Badge upload error details:', {
                                    message: error.message,
                                    response: error.response?.data,
                                    status: error.response?.status,
                                    badge: badge
                                });
                                message.error(`Failed to upload badge: ${error.response?.data?.message || error.message}`);
                                return null;
                            }
                        } else if (badge.url) {
                            console.log('Using existing badge url:', badge.url);
                            return {
                                url: badge.url,
                                x: (badge.x || 50) + percentOffsetX,
                                y: (badge.y || 50) + percentOffsetY,
                                size: badge.size || 80
                            };
                        }
                        return null;
                    })
            ).then(results => results.filter(Boolean));

            console.log('Uploaded badges result:', {
                badgesCount: uploadedBadges.length,
                uploadedBadges: uploadedBadges
            });

            if (removeCanva && !canvaFile) {
                canvaData = { enabled: false, originalFile: null, fileType: null, importDate: null };
            }

            // Upload watermark
            let watermarkPath = editingTemplate?.watermark?.image || null;
            if (watermarkFile) {
                const uploadedWatermark = await uploadWatermark(watermarkFile);
                watermarkPath = uploadedWatermark.path;
            } else if (removeWatermark) {
                watermarkPath = null;
            }

            const templateData = {
                name: values.name,
                isDefault: values.isDefault || false,
                design: {
                    layout: values.layout,
                    orientation: values.orientation,
                    backgroundColor: values.backgroundColor,
                    backgroundImage: backgroundPath,
                    borderStyle: values.borderStyle,
                    borderColor: values.borderColor,
                    secondaryBorderColor: values.secondaryBorderColor,
                    borderPattern: values.borderPattern,
                    borderWidth: values.borderWidth,
                    cornerDecoration: {
                        enabled: values.cornerDecorationEnabled || false,
                        type: values.cornerDecorationType || 'floral',
                        size: values.cornerDecorationSize || 80,
                        color: values.cornerDecorationColor || '#FFD700'
                    },
                    frameDecoration: {
                        enabled: values.fullFrameEnabled || false,
                        style: values.frameStyle || 'vintage_border',
                        color: values.frameColor || '#8B4513',
                        thickness: values.frameThickness || 2
                    },
                    layoutSettings: {
                        contentPadding: values.contentPadding ?? 30,
                        headerAlign: values.headerAlign || 'center',
                        titleAlign: values.titleAlign || 'center',
                        bodyAlign: values.bodyAlign || 'center',
                        footerAlign: values.footerAlign || 'center',
                        headerSpacing: values.headerSpacing ?? 30,
                        titleSpacing: values.titleSpacing ?? 20,
                        bodySpacing: values.bodySpacing ?? 30,
                        footerSpacing: values.footerSpacing ?? 50,
                        bodyMaxWidth: values.bodyMaxWidth ?? 600,
                        footerMaxWidth: values.footerMaxWidth ?? 600,
                        logoAlign: values.logoAlign || 'center',
                        logoMaxWidth: values.logoMaxWidth ?? 150,
                        logoMaxHeight: values.logoMaxHeight ?? 100,
                        logoOffsetTop: values.logoOffsetTop ?? 0,
                        logoOffsetX: values.logoOffsetX ?? 0,
                        logoOffsetY: values.logoOffsetY ?? 0,
                        headerOffsetX: values.headerOffsetX ?? 0,
                        headerOffsetY: values.headerOffsetY ?? 0,
                        titleOffsetX: values.titleOffsetX ?? 0,
                        titleOffsetY: values.titleOffsetY ?? 0,
                        bodyOffsetX: values.bodyOffsetX ?? 0,
                        bodyOffsetY: values.bodyOffsetY ?? 0,
                        footerOffsetX: values.footerOffsetX ?? 0,
                        footerOffsetY: values.footerOffsetY ?? 0
                    }
                },
                header: {
                    logo: logoPath,
                    instituteName: values.instituteName,
                    instituteNameFont: {
                        family: values.instituteNameFontFamily,
                        size: values.instituteNameFontSize,
                        color: values.instituteNameFontColor
                    }
                },
                body: {
                    title: {
                        text: values.titleText,
                        font: {
                            family: values.titleFontFamily,
                            size: values.titleFontSize,
                            color: values.titleFontColor
                        }
                    },
                    text: {
                        template: values.bodyTemplate,
                        font: {
                            family: values.bodyFontFamily,
                            size: values.bodyFontSize,
                            weight: values.bodyFontWeight || 'normal',
                            color: values.bodyFontColor
                        }
                    }
                },
                signatureSlots: signatureSlots,
                badges: uploadedBadges,
                watermark: removeWatermark ? null : (watermarkPath ? {
                    image: watermarkPath,
                    opacity: values.watermarkOpacity || 0.1,
                    size: values.watermarkSize || 300
                } : editingTemplate?.watermark || null),
                placeholders: availablePlaceholders,
                placeholderStyles: placeholderStyles,
                footer: {
                    showDate: values.showDate,
                    showSignature: values.showSignature,
                    signatureLabel: values.signatureLabel,
                    signatureImage: signaturePath,
                    issueDate: values.issueDate
                },
                canvaImport: canvaData
            };

            console.log('Template data being sent to backend:', {
                name: templateData.name,
                hasBadges: !!templateData.badges,
                badgesCount: templateData.badges?.length || 0,
                badges: templateData.badges,
                fullTemplateData: templateData
            });

            if (editingTemplate) {
                updateMutation.mutate({ id: editingTemplate._id, values: templateData });
            } else {
                createMutation.mutate(templateData);
            }
        } catch (error) {
            message.error(t('errorUploadingFiles'));
        }
    };

    const columns = [
        {
            title: t('name'),
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    {text}
                    {record.isDefault && <StarFilled style={{ color: '#faad14' }} />}
                </Space>
            )
        },
        {
            title: t('layout'),
            dataIndex: ['design', 'layout'],
            key: 'layout',
            render: (layout) => <Tag color="blue">{layout}</Tag>
        },
        {
            title: t('orientation'),
            dataIndex: ['design', 'orientation'],
            key: 'orientation',
            render: (orientation) => <Tag color="green">{orientation}</Tag>
        },
        {
            title: t('status'),
            dataIndex: 'isDefault',
            key: 'isDefault',
            render: (isDefault) => (
                <Tag color={isDefault ? 'gold' : 'default'}>
                    {isDefault ? t('default') : t('custom')}
                </Tag>
            )
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title={t('preview')}>
                        <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => handlePreview(record)}
                        />
                    </Tooltip>
                    <Tooltip title={t('print')}>
                        <Button
                            size="small"
                            icon={<PrinterOutlined />}
                            onClick={() => handlePrint(record)}
                        />
                    </Tooltip>
                    <Tooltip title={t('edit')}>
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    {!record.isDefault && (
                        <Tooltip title={t('setDefault')}>
                            <Button
                                size="small"
                                icon={<StarOutlined />}
                                onClick={() => handleSetDefault(record._id)}
                            />
                        </Tooltip>
                    )}
                    {!record.isDefault && (
                        <Tooltip title={t('delete')}>
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDelete(record._id)}
                            />
                        </Tooltip>
                    )}
                </Space>
            )
        }
    ];

    const fontFamilies = ['Arial', 'Times New Roman', 'Georgia', 'Courier New', 'Verdana', 'Helvetica'];

    return (
        <div>
            <div className="page-header">
                <Title level={2} style={{ margin: 0 }}>{t('certificateTemplates')}</Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                    {t('createTemplate')}
                </Button>
            </div>

            <Card>
                <Table
                    columns={columns}
                    dataSource={templatesData}
                    rowKey="_id"
                    loading={isLoading}
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            {/* Create/Edit Modal */}
            <Modal
                title={editingTemplate ? t('editTemplate') : t('createTemplate')}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingTemplate(null);
                    form.resetFields();
                }}
                footer={null}
                width="95%"
                style={{ top: 20 }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    onValuesChange={(changedValues, allValues) => {
                        // Update live preview whenever any field changes
                        setLivePreviewData(allValues);
                    }}
                >
                    <Tabs defaultActiveKey="1">
                        {/* Basic Info */}
                        <Tabs.TabPane tab={t('basicInfo')} key="1">
                            <Form.Item
                                name="name"
                                label={t('templateName')}
                                rules={[{ required: true, message: t('pleaseEnterTemplateName') }]}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item name="isDefault" label={t('setAsDefault')} valuePropName="checked">
                                <Switch />
                            </Form.Item>

                            <Divider>{t('design')}</Divider>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        name="layout"
                                        label={t('layout')}
                                        rules={[{ required: true }]}
                                        initialValue="classic"
                                    >
                                        <Select>
                                            <Option value="classic">{t('classic')}</Option>
                                            <Option value="modern">{t('modern')}</Option>
                                            <Option value="elegant">{t('elegant')}</Option>
                                            <Option value="minimal">{t('minimal')}</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="orientation"
                                        label={t('orientation')}
                                        rules={[{ required: true }]}
                                        initialValue="landscape"
                                    >
                                        <Select>
                                            <Option value="landscape">{t('landscape')}</Option>
                                            <Option value="portrait">{t('portrait')}</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="borderWidth"
                                        label={t('borderWidth')}
                                        initialValue={2}
                                    >
                                        <InputNumber min={0} max={10} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        name="borderStyle"
                                        label={t('borderStyle')}
                                        initialValue="single"
                                    >
                                        <Select>
                                            <Option value="none">{t('none')}</Option>
                                            <Option value="single">{t('single')}</Option>
                                            <Option value="double">{t('double')}</Option>
                                            <Option value="triple">{t('triple')}</Option>
                                            <Option value="ornate">{t('ornate')}</Option>
                                            <Option value="dashed">{t('dashed')}</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="backgroundColor"
                                        label={t('backgroundColor')}
                                        initialValue="#FFFFFF"
                                    >
                                        <Input type="color" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="borderColor"
                                        label={t('borderColor')}
                                        initialValue="#000000"
                                    >
                                        <Input type="color" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="secondaryBorderColor"
                                        label={t('secondaryBorderColor')}
                                        initialValue="#DAA520"
                                    >
                                        <Input type="color" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="borderPattern"
                                        label={t('borderPattern')}
                                        initialValue="none"
                                    >
                                        <Select>
                                            <Option value="none">{t('none')}</Option>
                                            <Option value="dots">{t('dots')}</Option>
                                            <Option value="waves">{t('waves')}</Option>
                                            <Option value="zigzag">{t('zigzag')}</Option>
                                            <Option value="chain">{t('chain')}</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item label={t('backgroundImage')}>
                                <Upload
                                    beforeUpload={(file) => {
                                        setBackgroundFile(file);
                                        setRemoveBackground(false);
                                        return false;
                                    }}
                                    maxCount={1}
                                    accept="image/*"
                                >
                                    <Button icon={<UploadOutlined />}>{t('uploadBackground')}</Button>
                                </Upload>
                                {backgroundFile && <Text type="secondary">{backgroundFile.name}</Text>}
                                {!backgroundFile && editingTemplate?.design?.backgroundImage && (
                                    <Text type="secondary">{t('currentBackground')}: {editingTemplate.design.backgroundImage}</Text>
                                )}
                                <div style={{ marginTop: 8 }}>
                                    <Button
                                        danger
                                        size="small"
                                        disabled={!editingTemplate?.design?.backgroundImage && !backgroundFile}
                                        onClick={() => {
                                            setRemoveBackground(true);
                                            setBackgroundFile(null);
                                            message.info(t('backgroundRemoved'));
                                        }}
                                    >
                                        {t('removeBackground')}
                                    </Button>
                                </div>
                            </Form.Item>

                            <Divider>{t('frameDecorations')}</Divider>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="fullFrameEnabled"
                                        label={t('enableFullFrame')}
                                        valuePropName="checked"
                                        initialValue={false}
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="cornerDecorationEnabled"
                                        label={t('enableCorners')}
                                        valuePropName="checked"
                                        initialValue={false}
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) =>
                                    prevValues.fullFrameEnabled !== currentValues.fullFrameEnabled
                                }
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('fullFrameEnabled') ? (
                                        <>
                                            <Form.Item
                                                name="frameStyle"
                                                label={t('frameStyle')}
                                                initialValue="vintage_border"
                                            >
                                                <Select>
                                                    <Option value="vintage_border">{t('vintageBorder')}</Option>
                                                    <Option value="elegant_frame">{t('elegantFrame')}</Option>
                                                    <Option value="ornate_full">{t('ornateFull')}</Option>
                                                    <Option value="art_deco_frame">{t('artDecoFrame')}</Option>
                                                    <Option value="baroque_frame">{t('baroqueFrame')}</Option>
                                                    <Option value="modern_frame">{t('modernFrame')}</Option>
                                                    <Option value="islamic_frame">{t('islamicFrame')}</Option>
                                                    <Option value="modern_islamic">{t('modernIslamic')}</Option>
                                                    <Option value="classic_islamic">{t('classicIslamic')}</Option>
                                                    <Option value="modern_ornate">{t('modernOrnate')}</Option>
                                                    <Option value="special">Special</Option>
                                                </Select>
                                            </Form.Item>
                                            <Row gutter={16}>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="frameColor"
                                                        label={t('frameColor')}
                                                        initialValue="#8B4513"
                                                    >
                                                        <Input type="color" />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="frameThickness"
                                                        label={t('frameThickness')}
                                                        initialValue={2}
                                                    >
                                                        <InputNumber min={1} max={5} style={{ width: '100%' }} />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </>
                                    ) : null
                                }
                            </Form.Item>

                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) =>
                                    prevValues.cornerDecorationEnabled !== currentValues.cornerDecorationEnabled
                                }
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('cornerDecorationEnabled') ? (
                                        <>
                                            <Row gutter={16}>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="cornerDecorationType"
                                                        label={t('decorationType')}
                                                        initialValue="floral"
                                                    >
                                                        <Select>
                                                            <Option value="floral">{t('floral')}</Option>
                                                            <Option value="geometric">{t('geometric')}</Option>
                                                            <Option value="ornamental">{t('ornamental')}</Option>
                                                            <Option value="islamic">{t('islamic')}</Option>
                                                            <Option value="arabesque">{t('arabesque')}</Option>
                                                            <Option value="baroque">{t('baroque')}</Option>
                                                            <Option value="celtic">{t('celtic')}</Option>
                                                            <Option value="art_deco">{t('artDeco')}</Option>
                                                            <Option value="vintage">{t('vintage')}</Option>
                                                            <Option value="modern_curves">{t('modernCurves')}</Option>
                                                            <Option value="elegant_swirl">{t('elegantSwirl')}</Option>
                                                            <Option value="royal">{t('royal')}</Option>
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="cornerDecorationColor"
                                                        label={t('decorationColor')}
                                                        initialValue="#FFD700"
                                                    >
                                                        <Input type="color" />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <Form.Item
                                                name="cornerDecorationSize"
                                                label={t('decorationSize')}
                                                initialValue={80}
                                            >
                                                <InputNumber min={40} max={150} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </>
                                    ) : null
                                }
                            </Form.Item>

                            <Divider>{t('layoutSettings')}</Divider>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name="contentPadding" label={t('contentPadding')} initialValue={30}>
                                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="bodyMaxWidth" label={t('bodyMaxWidth')} initialValue={600}>
                                        <InputNumber min={300} max={900} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="footerMaxWidth" label={t('footerMaxWidth')} initialValue={600}>
                                        <InputNumber min={300} max={900} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={6}>
                                    <Form.Item name="headerAlign" label={t('headerAlign')} initialValue="center">
                                        <Select>
                                            <Option value="left">{t('left')}</Option>
                                            <Option value="center">{t('center')}</Option>
                                            <Option value="right">{t('right')}</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name="titleAlign" label={t('titleAlign')} initialValue="center">
                                        <Select>
                                            <Option value="left">{t('left')}</Option>
                                            <Option value="center">{t('center')}</Option>
                                            <Option value="right">{t('right')}</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name="bodyAlign" label={t('bodyAlign')} initialValue="center">
                                        <Select>
                                            <Option value="left">{t('left')}</Option>
                                            <Option value="center">{t('center')}</Option>
                                            <Option value="right">{t('right')}</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name="footerAlign" label={t('footerAlign')} initialValue="center">
                                        <Select>
                                            <Option value="left">{t('left')}</Option>
                                            <Option value="center">{t('center')}</Option>
                                            <Option value="right">{t('right')}</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={6}>
                                    <Form.Item name="headerSpacing" label={t('headerSpacing')} initialValue={30}>
                                        <InputNumber min={0} max={120} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name="titleSpacing" label={t('titleSpacing')} initialValue={20}>
                                        <InputNumber min={0} max={120} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name="bodySpacing" label={t('bodySpacing')} initialValue={30}>
                                        <InputNumber min={0} max={120} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name="footerSpacing" label={t('footerSpacing')} initialValue={50}>
                                        <InputNumber min={0} max={200} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Tabs.TabPane>

                        {/* Header */}
                        <Tabs.TabPane tab={t('header')} key="2">
                            <Form.Item label={t('instituteLogo')}>
                                <Upload
                                    beforeUpload={(file) => {
                                        setLogoFile(file);
                                        setRemoveLogo(false);
                                        return false;
                                    }}
                                    maxCount={1}
                                    accept="image/*"
                                >
                                    <Button icon={<UploadOutlined />}>{t('uploadLogo')}</Button>
                                </Upload>
                                {logoFile && <Text type="secondary">{logoFile.name}</Text>}
                                {!logoFile && editingTemplate?.header?.logo && (
                                    <Text type="secondary">{t('currentLogo')}: {editingTemplate.header.logo}</Text>
                                )}
                                <div style={{ marginTop: 8 }}>
                                    <Button
                                        danger
                                        size="small"
                                        disabled={!editingTemplate?.header?.logo && !logoFile}
                                        onClick={() => {
                                            setRemoveLogo(true);
                                            setLogoFile(null);
                                            message.info(t('logoRemoved'));
                                        }}
                                    >
                                        {t('removeLogo')}
                                    </Button>
                                </div>
                            </Form.Item>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name="logoAlign" label={t('logoAlign')} initialValue="center">
                                        <Select>
                                            <Option value="left">{t('left')}</Option>
                                            <Option value="center">{t('center')}</Option>
                                            <Option value="right">{t('right')}</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="logoMaxWidth" label={t('logoMaxWidth')} initialValue={150}>
                                        <InputNumber min={50} max={300} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="logoMaxHeight" label={t('logoMaxHeight')} initialValue={100}>
                                        <InputNumber min={30} max={200} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name="logoOffsetTop" label={t('logoOffsetTop')} initialValue={0}>
                                        <InputNumber min={-100} max={200} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                name="instituteName"
                                label={t('instituteName')}
                                rules={[{ required: true }]}
                            >
                                <Input />
                            </Form.Item>

                            <Divider>{t('instituteNameFont')}</Divider>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        name="instituteNameFontFamily"
                                        label={t('fontFamily')}
                                        initialValue="Arial"
                                    >
                                        <Select>
                                            {fontFamilies.map(font => (
                                                <Option key={font} value={font}>{font}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="instituteNameFontSize"
                                        label={t('fontSize')}
                                        initialValue={24}
                                    >
                                        <InputNumber min={10} max={60} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="instituteNameFontColor"
                                        label={t('fontColor')}
                                        initialValue="#000000"
                                    >
                                        <Input type="color" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Tabs.TabPane>

                        {/* Body */}
                        <Tabs.TabPane tab={t('body')} key="3">
                            <Form.Item
                                name="titleText"
                                label={t('certificateTitle')}
                                rules={[{ required: true }]}
                                initialValue="Certificate of Completion"
                            >
                                <Input />
                            </Form.Item>

                            <Divider>{t('titleFont')}</Divider>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        name="titleFontFamily"
                                        label={t('fontFamily')}
                                        initialValue="Arial"
                                    >
                                        <Select>
                                            {fontFamilies.map(font => (
                                                <Option key={font} value={font}>{font}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="titleFontSize"
                                        label={t('fontSize')}
                                        initialValue={36}
                                    >
                                        <InputNumber min={20} max={80} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="titleFontColor"
                                        label={t('fontColor')}
                                        initialValue="#000000"
                                    >
                                        <Input type="color" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider>{t('bodyText')}</Divider>

                            {/* Placeholder insertion chips */}
                            <div style={{ marginBottom: '12px' }}>
                                <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
                                    {t('clickToInsertPlaceholder')}:
                                </Text>
                                <Space wrap>
                                    {availablePlaceholders.map((placeholder) => (
                                        <Tag
                                            key={placeholder}
                                            color="blue"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                const currentValue = form.getFieldValue('bodyTemplate') || '';
                                                form.setFieldsValue({
                                                    bodyTemplate: currentValue + ' ' + placeholder
                                                });
                                            }}
                                        >
                                            {placeholder}
                                        </Tag>
                                    ))}
                                </Space>
                            </div>

                            <Form.Item
                                name="bodyTemplate"
                                label={t('template')}
                                help={
                                    <div>
                                        <div>{t('useVariables')}</div>
                                        <div style={{ marginTop: 4, fontSize: 11, color: '#1890ff' }}>
                                            Tip: Use HTML tags for formatting: &lt;b&gt;bold&lt;/b&gt;, &lt;i&gt;italic&lt;/i&gt;, &lt;u&gt;underline&lt;/u&gt;, &lt;br/&gt; for line break
                                        </div>
                                    </div>
                                }
                                initialValue="This is to certify that {{studentName}} has successfully completed the {{courseName}} course."
                            >
                                <TextArea rows={4} />
                            </Form.Item>

                            <Divider>{t('placeholderStyling')}</Divider>

                            {/* Placeholder Styling Section */}
                            <div style={{ marginBottom: '24px' }}>
                                <Text type="secondary" style={{ fontSize: 12, marginBottom: 12, display: 'block' }}>
                                    {t('customizePlaceholderStyle')}
                                </Text>
                                {availablePlaceholders.map((placeholder) => {
                                    const currentStyle = placeholderStyles[placeholder] || {};
                                    return (
                                        <Card key={placeholder} size="small" style={{ marginBottom: 12 }}>
                                            <Row gutter={[8, 8]} align="middle">
                                                <Col span={24}>
                                                    <Tag color="geekblue">{placeholder}</Tag>
                                                </Col>
                                                <Col span={6}>
                                                    <Select
                                                        size="small"
                                                        placeholder={t('font')}
                                                        value={currentStyle.fontFamily}
                                                        onChange={(value) => {
                                                            setPlaceholderStyles({
                                                                ...placeholderStyles,
                                                                [placeholder]: { ...currentStyle, fontFamily: value }
                                                            });
                                                        }}
                                                        style={{ width: '100%' }}
                                                        allowClear
                                                    >
                                                        <Option value="Arial">Arial</Option>
                                                        <Option value="Times New Roman">Times New Roman</Option>
                                                        <Option value="Georgia">Georgia</Option>
                                                        <Option value="Courier New">Courier New</Option>
                                                        <Option value="Verdana">Verdana</Option>
                                                    </Select>
                                                </Col>
                                                <Col span={4}>
                                                    <InputNumber
                                                        size="small"
                                                        placeholder={t('size')}
                                                        value={currentStyle.fontSize}
                                                        onChange={(value) => {
                                                            setPlaceholderStyles({
                                                                ...placeholderStyles,
                                                                [placeholder]: { ...currentStyle, fontSize: value }
                                                            });
                                                        }}
                                                        min={8}
                                                        max={72}
                                                        style={{ width: '100%' }}
                                                    />
                                                </Col>
                                                <Col span={3}>
                                                    <Input
                                                        size="small"
                                                        type="color"
                                                        value={currentStyle.color || '#000000'}
                                                        onChange={(e) => {
                                                            setPlaceholderStyles({
                                                                ...placeholderStyles,
                                                                [placeholder]: { ...currentStyle, color: e.target.value }
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                                <Col span={11}>
                                                    <Space size="small">
                                                        <Tooltip title={t('bold')}>
                                                            <Button
                                                                size="small"
                                                                type={currentStyle.fontWeight === 'bold' ? 'primary' : 'default'}
                                                                onClick={() => {
                                                                    setPlaceholderStyles({
                                                                        ...placeholderStyles,
                                                                        [placeholder]: {
                                                                            ...currentStyle,
                                                                            fontWeight: currentStyle.fontWeight === 'bold' ? 'normal' : 'bold'
                                                                        }
                                                                    });
                                                                }}
                                                                style={{ fontWeight: 'bold' }}
                                                            >
                                                                B
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip title={t('italic')}>
                                                            <Button
                                                                size="small"
                                                                type={currentStyle.fontStyle === 'italic' ? 'primary' : 'default'}
                                                                onClick={() => {
                                                                    setPlaceholderStyles({
                                                                        ...placeholderStyles,
                                                                        [placeholder]: {
                                                                            ...currentStyle,
                                                                            fontStyle: currentStyle.fontStyle === 'italic' ? 'normal' : 'italic'
                                                                        }
                                                                    });
                                                                }}
                                                                style={{ fontStyle: 'italic' }}
                                                            >
                                                                I
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip title={t('underline')}>
                                                            <Button
                                                                size="small"
                                                                type={currentStyle.textDecoration === 'underline' ? 'primary' : 'default'}
                                                                onClick={() => {
                                                                    setPlaceholderStyles({
                                                                        ...placeholderStyles,
                                                                        [placeholder]: {
                                                                            ...currentStyle,
                                                                            textDecoration: currentStyle.textDecoration === 'underline' ? 'none' : 'underline'
                                                                        }
                                                                    });
                                                                }}
                                                                style={{ textDecoration: 'underline' }}
                                                            >
                                                                U
                                                            </Button>
                                                        </Tooltip>
                                                        <Select
                                                            size="small"
                                                            placeholder={t('case')}
                                                            value={currentStyle.textTransform}
                                                            onChange={(value) => {
                                                                setPlaceholderStyles({
                                                                    ...placeholderStyles,
                                                                    [placeholder]: { ...currentStyle, textTransform: value }
                                                                });
                                                            }}
                                                            style={{ width: '100px' }}
                                                            allowClear
                                                        >
                                                            <Option value="uppercase">{t('uppercase')}</Option>
                                                            <Option value="lowercase">{t('lowercase')}</Option>
                                                            <Option value="capitalize">{t('capitalize')}</Option>
                                                        </Select>
                                                    </Space>
                                                </Col>
                                            </Row>
                                        </Card>
                                    );
                                })}
                            </div>

                            <Divider>{t('bodyFont')}</Divider>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        name="bodyFontFamily"
                                        label={t('fontFamily')}
                                        initialValue="Arial"
                                    >
                                        <Select>
                                            {fontFamilies.map(font => (
                                                <Option key={font} value={font}>{font}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="bodyFontSize"
                                        label={t('fontSize')}
                                        initialValue={16}
                                    >
                                        <InputNumber min={10} max={30} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="bodyFontWeight"
                                        label={t('fontWeight')}
                                        initialValue="normal"
                                    >
                                        <Select>
                                            <Option value="normal">Normal</Option>
                                            <Option value="bold">Bold</Option>
                                            <Option value="600">Semi Bold</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="bodyFontColor"
                                        label={t('fontColor')}
                                        initialValue="#333333"
                                    >
                                        <Input type="color" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider>{t('badges')}</Divider>
                            {badges.map((badge, index) => (
                                <Card key={index} size="small" style={{ marginBottom: 16 }}>
                                    <Row gutter={16}>
                                        <Col span={16}>
                                            <Form.Item label={t('badgeImage')}>
                                                {badge.url && !badge.file && (
                                                    <div style={{ marginBottom: 8 }}>
                                                        <img src={`${config.baseUrl}${badge.url}`} alt="Current badge" style={{ maxWidth: 100, maxHeight: 100, objectFit: 'contain' }} />
                                                        <div style={{ fontSize: 12, color: '#888' }}>Current badge - upload new to replace</div>
                                                    </div>
                                                )}
                                                <Input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            const newBadges = [...badges];
                                                            newBadges[index].file = file;
                                                            console.log('Badge file added to state:', { index, fileName: file.name, totalBadges: newBadges.length, badge: newBadges[index] });
                                                            setBadges(newBadges);
                                                        }
                                                    }}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item label={t('size')}>
                                                <InputNumber
                                                    min={20}
                                                    max={150}
                                                    value={badge.size || 80}
                                                    onChange={(value) => {
                                                        const newBadges = [...badges];
                                                        newBadges[index].size = value;
                                                        setBadges(newBadges);
                                                    }}
                                                    style={{ width: '100%' }}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Button
                                        danger
                                        size="small"
                                        onClick={() => setBadges(badges.filter((_, i) => i !== index))}
                                    >
                                        {t('remove')}
                                    </Button>
                                </Card>
                            ))}
                            <Button
                                type="dashed"
                                onClick={() => setBadges([...badges, { x: 50, y: 50, size: 80 }])}
                                block
                            >
                                + {t('addBadge')}
                            </Button>

                            <Divider>{t('watermark')}</Divider>
                            <Card size="small" style={{ marginBottom: 16 }}>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item label={t('watermarkImage')}>
                                            {editingTemplate?.watermark?.image && !watermarkFile && !removeWatermark && (
                                                <div style={{ marginBottom: 8 }}>
                                                    <img
                                                        src={`${config.baseUrl}${editingTemplate.watermark.image}`}
                                                        alt="Current watermark"
                                                        style={{ maxWidth: 150, maxHeight: 150, objectFit: 'contain', opacity: 0.5 }}
                                                    />
                                                    <div style={{ fontSize: 12, color: '#888' }}>Current watermark - upload new to replace</div>
                                                </div>
                                            )}
                                            <Input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        setWatermarkFile(file);
                                                        setRemoveWatermark(false);
                                                        console.log('Watermark file selected:', file.name);
                                                    }
                                                }}
                                            />
                                            {(editingTemplate?.watermark?.image || watermarkFile) && (
                                                <Button
                                                    danger
                                                    size="small"
                                                    style={{ marginTop: 8 }}
                                                    onClick={() => {
                                                        setWatermarkFile(null);
                                                        setRemoveWatermark(true);
                                                    }}
                                                >
                                                    {t('removeWatermark')}
                                                </Button>
                                            )}
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="watermarkOpacity"
                                            label={t('opacity')}
                                            initialValue={0.1}
                                        >
                                            <Slider
                                                min={0}
                                                max={1}
                                                step={0.1}
                                                marks={{ 0: '0%', 0.5: '50%', 1: '100%' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="watermarkSize"
                                            label={t('size')}
                                            initialValue={300}
                                        >
                                            <InputNumber
                                                min={100}
                                                max={600}
                                                step={50}
                                                style={{ width: '100%' }}
                                                addonAfter="px"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Tabs.TabPane>

                        {/* Footer */}
                        <Tabs.TabPane tab={t('footer')} key="4">
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="showDate"
                                        label={t('showDate')}
                                        valuePropName="checked"
                                        initialValue={true}
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                name="issueDate"
                                label={t('issueDate')}
                                tooltip="Leave empty to use dynamic date (current date). Enter a specific date to make it static."
                            >
                                <Input placeholder="e.g., January 2026, 2026-01-25, or leave empty for current date" />
                            </Form.Item>


                            <Divider>{t('signatureSlots')} (1-4)</Divider>

                            {/* Signature Slots Management */}
                            <div style={{ marginBottom: 16 }}>
                                {signatureSlots.map((slot, index) => (
                                    <Card key={index} size="small" style={{ marginBottom: 12 }}>
                                        <Row gutter={16} align="middle" style={{ marginBottom: 8 }}>
                                            <Col span={12}>
                                                <Input
                                                    placeholder={t('signatureLabelPlaceholder') || 'Signature Label (e.g., Director)'}
                                                    value={slot.label}
                                                    onChange={(e) => {
                                                        const newSlots = [...signatureSlots];
                                                        newSlots[index].label = e.target.value;
                                                        setSignatureSlots(newSlots);
                                                    }}
                                                />
                                            </Col>
                                            <Col span={12}>
                                                <Input
                                                    placeholder="Signer Name (e.g., John Smith)"
                                                    value={slot.signerName || ''}
                                                    onChange={(e) => {
                                                        const newSlots = [...signatureSlots];
                                                        newSlots[index].signerName = e.target.value;
                                                        setSignatureSlots(newSlots);
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                        <Row gutter={16} align="middle">
                                            <Col span={8}>
                                                <Select
                                                    value={slot.position}
                                                    onChange={(value) => {
                                                        const newSlots = [...signatureSlots];
                                                        newSlots[index].position = value;
                                                        setSignatureSlots(newSlots);
                                                    }}
                                                    style={{ width: '100%' }}
                                                >
                                                    <Option value="left">{t('left')}</Option>
                                                    <Option value="center">{t('center')}</Option>
                                                    <Option value="right">{t('right')}</Option>
                                                    <Option value="custom">{t('custom')}</Option>
                                                </Select>
                                            </Col>
                                            <Col span={5}>
                                                <Switch
                                                    checked={slot.showLabel}
                                                    onChange={(checked) => {
                                                        const newSlots = [...signatureSlots];
                                                        newSlots[index].showLabel = checked;
                                                        setSignatureSlots(newSlots);
                                                    }}
                                                    checkedChildren={t('label')}
                                                    unCheckedChildren={t('noLabel')}
                                                />
                                            </Col>
                                            <Col span={5}>
                                                <Switch
                                                    checked={slot.showSignerName || false}
                                                    onChange={(checked) => {
                                                        const newSlots = [...signatureSlots];
                                                        newSlots[index].showSignerName = checked;
                                                        setSignatureSlots(newSlots);
                                                    }}
                                                    checkedChildren="Name"
                                                    unCheckedChildren="No Name"
                                                />
                                            </Col>
                                            <Col span={6}>
                                                {signatureSlots.length > 1 && (
                                                    <Button
                                                        danger
                                                        size="small"
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => {
                                                            const newSlots = signatureSlots.filter((_, i) => i !== index);
                                                            setSignatureSlots(newSlots);
                                                        }}
                                                    >
                                                        {t('remove')}
                                                    </Button>
                                                )}
                                            </Col>
                                        </Row>
                                    </Card>
                                ))}
                                {signatureSlots.length < 4 && (
                                    <Button
                                        type="dashed"
                                        block
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                            setSignatureSlots([
                                                ...signatureSlots,
                                                {
                                                    label: t('signature'),
                                                    position: 'center',
                                                    showLabel: true
                                                }
                                            ]);
                                        }}
                                    >
                                        {t('addSignatureSlot', { count: signatureSlots.length })}
                                    </Button>
                                )}
                            </div>
                        </Tabs.TabPane>

                        {/* Canva Import Tab */}
                        {/* <Tabs.TabPane tab={<span><FileImageOutlined /> {t('canvaImport')}</span>} key="5">
                            <Title level={5}>{t('importFromCanva')}</Title>
                            <Text type="secondary">
                                {t('uploadCanvaExport')} (PNG, SVG, PDF, JPG)
                            </Text>

                            <div style={{ marginTop: 16 }}>
                                <Upload
                                    beforeUpload={(file) => {
                                        setCanvaFile(file);
                                        message.success(`${file.name} selected for upload`);
                                        return false;
                                    }}
                                    maxCount={1}
                                    accept=".png,.svg,.pdf,.jpg,.jpeg"
                                >
                                    <Button icon={<UploadOutlined />} size="large">
                                        {t('selectCanvaFile')}
                                    </Button>
                                </Upload>
                                {canvaFile && (
                                    <div style={{ marginTop: 12 }}>
                                        <Tag color="success" icon={<FileImageOutlined />}>
                                            {canvaFile.name}
                                        </Tag>
                                        <Text type="secondary" style={{ marginLeft: 8 }}>
                                            {(canvaFile.size / 1024).toFixed(2)} KB
                                        </Text>
                                    </div>
                                )}
                                {!canvaFile && editingTemplate?.canvaImport?.originalFile && (
                                    <div style={{ marginTop: 12 }}>
                                        <Tag color="blue">
                                            {t('currentCanvaFile')}: {editingTemplate.canvaImport.originalFile}
                                        </Tag>
                                    </div>
                                )}
                            </div>

                            <Divider />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {t('canvaImportNote')}: The uploaded file will be used as the certificate background.
                                You can still add dynamic text placeholders and signature slots on top of it.
                            </Text>
                        </Tabs.TabPane> */}
                    </Tabs>

                    {/* Hidden fields for live preview offsets */}
                    <Form.Item name="logoOffsetX" hidden />
                    <Form.Item name="logoOffsetY" hidden />
                    <Form.Item name="headerOffsetX" hidden />
                    <Form.Item name="headerOffsetY" hidden />
                    <Form.Item name="titleOffsetX" hidden />
                    <Form.Item name="titleOffsetY" hidden />
                    <Form.Item name="bodyOffsetX" hidden />
                    <Form.Item name="bodyOffsetY" hidden />
                    <Form.Item name="footerOffsetX" hidden />
                    <Form.Item name="footerOffsetY" hidden />

                    <Divider />

                    {/* Live Preview Section */}
                    <Title level={5}>{t('livePreview')}</Title>
                    <Space style={{ marginBottom: 12 }}>
                        <Button
                            type={previewEditMode ? 'primary' : 'default'}
                            onClick={() => {
                                setPreviewEditMode(!previewEditMode);
                                setPreviewOffsets({});
                            }}
                        >
                            {previewEditMode ? t('editing') : t('editLivePreview')}
                        </Button>
                        {previewEditMode && (
                            <>
                                <Button
                                    type="primary"
                                    onClick={() => {
                                        const updatedValues = {};
                                        Object.keys(offsetFields).forEach((key) => {
                                            const xField = offsetFields[key].x;
                                            const yField = offsetFields[key].y;
                                            updatedValues[xField] = previewOffsets?.[key]?.x ?? getOffsetValue(key, 'x');
                                            updatedValues[yField] = previewOffsets?.[key]?.y ?? getOffsetValue(key, 'y');
                                        });
                                        form.setFieldsValue(updatedValues);
                                        setLivePreviewData({
                                            ...(livePreviewData || {}),
                                            ...updatedValues
                                        });

                                        // Update badge positions based on preview offsets
                                        const orientation = form.getFieldValue('orientation') || 'portrait';
                                        const containerWidth = orientation === 'landscape' ? 1122 : 793;
                                        const containerHeight = orientation === 'landscape' ? 793 : 1122;

                                        const updatedBadges = badges.map((badge, index) => {
                                            const badgeKey = `badge${index}`;
                                            const offsetX = previewOffsets[badgeKey]?.x || 0;
                                            const offsetY = previewOffsets[badgeKey]?.y || 0;

                                            if (offsetX === 0 && offsetY === 0) return badge;

                                            const percentOffsetX = (offsetX / containerWidth) * 100;
                                            const percentOffsetY = (offsetY / containerHeight) * 100;

                                            return {
                                                ...badge,
                                                x: (badge.x || 50) + percentOffsetX,
                                                y: (badge.y || 50) + percentOffsetY
                                            };
                                        });

                                        setBadges(updatedBadges);
                                        setPreviewEditMode(false);
                                        setPreviewOffsets({});
                                        message.success(t('previewApplied'));
                                    }}
                                >
                                    {t('apply')}
                                </Button>
                                <Button
                                    onClick={() => {
                                        setPreviewEditMode(false);
                                        setPreviewOffsets({});
                                        message.info(t('previewCanceled'));
                                    }}
                                >
                                    {t('cancel')}
                                </Button>
                            </>
                        )}
                    </Space>
                    <div
                        style={{
                            width: (livePreviewData?.orientation || form.getFieldValue('orientation')) === 'landscape' ? '297mm' : '210mm',
                            height: (livePreviewData?.orientation || form.getFieldValue('orientation')) === 'landscape' ? '210mm' : '297mm',
                            backgroundColor: livePreviewData?.backgroundColor || form.getFieldValue('backgroundColor') || '#FFFFFF',
                            backgroundImage: (() => {
                                // Priority: Canva file > Background file > Existing background/canva
                                if (canvaFile) {
                                    return `url(${URL.createObjectURL(canvaFile)})`;
                                }
                                if (backgroundFile) {
                                    return `url(${URL.createObjectURL(backgroundFile)})`;
                                }
                                if (!removeCanva && editingTemplate?.canvaImport?.enabled && editingTemplate?.canvaImport?.originalFile) {
                                    return `url(${config.baseUrl}${editingTemplate.canvaImport.originalFile})`;
                                }
                                if (!removeBackground && editingTemplate?.design?.backgroundImage) {
                                    return `url(${config.baseUrl}${editingTemplate.design.backgroundImage})`;
                                }
                                return 'none';
                            })(),
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            padding: `${livePreviewData?.contentPadding || form.getFieldValue('contentPadding') || 30}px`,
                            textAlign: 'left',
                            marginBottom: '20px',
                            position: 'relative',
                            boxShadow: (() => {
                                const borderStyle = livePreviewData?.borderStyle || form.getFieldValue('borderStyle') || 'single';
                                const borderColor = livePreviewData?.borderColor || form.getFieldValue('borderColor') || '#000000';
                                const secondaryColor = livePreviewData?.secondaryBorderColor || form.getFieldValue('secondaryBorderColor') || '#DAA520';

                                if (borderStyle === 'none') return 'none';
                                if (borderStyle === 'single') return `inset 0 0 0 2px ${borderColor}`;
                                if (borderStyle === 'double') return `inset 0 0 0 2px ${borderColor}, inset 0 0 0 6px ${secondaryColor}, inset 0 0 0 8px ${borderColor}`;
                                if (borderStyle === 'triple') return `inset 0 0 0 2px ${borderColor}, inset 0 0 0 5px ${secondaryColor}, inset 0 0 0 7px ${borderColor}, inset 0 0 0 10px ${secondaryColor}, inset 0 0 0 12px ${borderColor}`;
                                if (borderStyle === 'ornate') return `inset 0 0 0 1px ${borderColor}, inset 0 0 0 3px ${secondaryColor}, inset 0 0 0 4px ${borderColor}, inset 0 0 0 8px ${secondaryColor}, inset 0 0 0 10px ${borderColor}`;
                                return `inset 0 0 0 2px ${borderColor}`;
                            })(),
                            border: (() => {
                                const borderStyle = livePreviewData?.borderStyle || form.getFieldValue('borderStyle') || 'single';
                                const borderColor = livePreviewData?.borderColor || form.getFieldValue('borderColor') || '#000000';
                                if (borderStyle === 'dashed') return `3px dashed ${borderColor}`;
                                return 'none';
                            })()
                        }}
                    >
                        {/* Full Frame Decorations */}
                        {(livePreviewData?.fullFrameEnabled || form.getFieldValue('fullFrameEnabled')) && (() => {
                            const frameStyle = livePreviewData?.frameStyle || form.getFieldValue('frameStyle') || 'vintage_border';
                            const frameColor = livePreviewData?.frameColor || form.getFieldValue('frameColor') || '#8B4513';
                            const frameThickness = livePreviewData?.frameThickness || form.getFieldValue('frameThickness') || 2;

                            const framePatterns = {
                                // Luxury certificate with ornate corners and decorative borders
                                vintage_border: (
                                    <>
                                        {/* Triple border frame */}
                                        <rect x="1" y="1" width="98" height="98" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.5} />
                                        <rect x="3.5" y="3.5" width="93" height="93" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                        <rect x="6" y="6" width="88" height="88" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />

                                        {/* Ornate corner flourishes */}
                                        <path d="M7,7 Q7,12 10,12 Q12,12 12,10 Q12,7 7,7 M7,7 L12,12 M10,7 Q10,10 7,10" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />
                                        <path d="M93,7 Q93,12 90,12 Q88,12 88,10 Q88,7 93,7 M93,7 L88,12 M90,7 Q90,10 93,10" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />
                                        <path d="M7,93 Q7,88 10,88 Q12,88 12,90 Q12,93 7,93 M7,93 L12,88 M10,93 Q10,90 7,90" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />
                                        <path d="M93,93 Q93,88 90,88 Q88,88 88,90 Q88,93 93,93 M93,93 L88,88 M90,93 Q90,90 93,90" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />

                                        {/* Decorative swirls in corners */}
                                        <path d="M10,10 Q8,13 10,16 Q13,18 16,16 Q18,13 16,10 Q13,8 10,10" fill={frameColor} opacity="0.2" />
                                        <path d="M90,10 Q92,13 90,16 Q87,18 84,16 Q82,13 84,10 Q87,8 90,10" fill={frameColor} opacity="0.2" />
                                        <path d="M10,90 Q8,87 10,84 Q13,82 16,84 Q18,87 16,90 Q13,92 10,90" fill={frameColor} opacity="0.2" />
                                        <path d="M90,90 Q92,87 90,84 Q87,82 84,84 Q82,87 84,90 Q87,92 90,90" fill={frameColor} opacity="0.2" />

                                        {/* Side ornaments */}
                                        <circle cx="50" cy="4" r="1.5" fill={frameColor} />
                                        <circle cx="50" cy="96" r="1.5" fill={frameColor} />
                                        <circle cx="4" cy="50" r="1.5" fill={frameColor} />
                                        <circle cx="96" cy="50" r="1.5" fill={frameColor} />
                                    </>
                                ),

                                // Elegant scalloped frame with wave patterns
                                elegant_frame: (
                                    <>
                                        {/* Main decorative border */}
                                        <rect x="2" y="2" width="96" height="96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.2} />

                                        {/* Scalloped inner border - top */}
                                        <path d="M8,5 Q10,3 12,5 Q14,3 16,5 Q18,3 20,5 Q22,3 24,5 Q26,3 28,5 Q30,3 32,5 Q34,3 36,5 Q38,3 40,5 Q42,3 44,5 Q46,3 48,5 Q50,3 52,5 Q54,3 56,5 Q58,3 60,5 Q62,3 64,5 Q66,3 68,5 Q70,3 72,5 Q74,3 76,5 Q78,3 80,5 Q82,3 84,5 Q86,3 88,5 Q90,3 92,5"
                                            stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />

                                        {/* Bottom scallops */}
                                        <path d="M8,95 Q10,97 12,95 Q14,97 16,95 Q18,97 20,95 Q22,97 24,95 Q26,97 28,95 Q30,97 32,95 Q34,97 36,95 Q38,97 40,95 Q42,97 44,95 Q46,97 48,95 Q50,97 52,95 Q54,97 56,95 Q58,97 60,95 Q62,97 64,95 Q66,97 68,95 Q70,97 72,95 Q74,97 76,95 Q78,97 80,95 Q82,97 84,95 Q86,97 88,95 Q90,97 92,95"
                                            stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />

                                        {/* Left scallops */}
                                        <path d="M5,8 Q3,10 5,12 Q3,14 5,16 Q3,18 5,20 Q3,22 5,24 Q3,26 5,28 Q3,30 5,32 Q3,34 5,36 Q3,38 5,40 Q3,42 5,44 Q3,46 5,48 Q3,50 5,52 Q3,54 5,56 Q3,58 5,60 Q3,62 5,64 Q3,66 5,68 Q3,70 5,72 Q3,74 5,76 Q3,78 5,80 Q3,82 5,84 Q3,86 5,88 Q3,90 5,92"
                                            stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />

                                        {/* Right scallops */}
                                        <path d="M95,8 Q97,10 95,12 Q97,14 95,16 Q97,18 95,20 Q97,22 95,24 Q97,26 95,28 Q97,30 95,32 Q97,34 95,36 Q97,38 95,40 Q97,42 95,44 Q97,46 95,48 Q97,50 95,52 Q97,54 95,56 Q97,58 95,60 Q97,62 95,64 Q97,66 95,68 Q97,70 95,72 Q97,74 95,76 Q97,78 95,80 Q97,82 95,84 Q97,86 95,88 Q97,90 95,92"
                                            stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />

                                        {/* Corner embellishments */}
                                        <circle cx="8" cy="8" r="2" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="92" cy="8" r="2" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="8" cy="92" r="2" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="92" cy="92" r="2" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    </>
                                ),

                                // Ornate multi-layer frame with filigree
                                ornate_full: (
                                    <>
                                        {/* Outer ornate border */}
                                        <rect x="1" y="1" width="98" height="98" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.5} />
                                        <rect x="3" y="3" width="94" height="94" fill="none" stroke={frameColor} strokeWidth={frameThickness} />
                                        <rect x="5.5" y="5.5" width="89" height="89" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <rect x="8" y="8" width="84" height="84" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />

                                        {/* Elaborate corner designs */}
                                        <path d="M10,10 L15,10 L15,15 L10,15 Z M12.5,10 L12.5,15 M10,12.5 L15,12.5" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M90,10 L85,10 L85,15 L90,15 Z M87.5,10 L87.5,15 M85,12.5 L90,12.5" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M10,90 L15,90 L15,85 L10,85 Z M12.5,85 L12.5,90 M10,87.5 L15,87.5" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M90,90 L85,90 L85,85 L90,85 Z M87.5,85 L87.5,90 M85,87.5 L90,87.5" stroke={frameColor} strokeWidth={frameThickness * 0.5} />

                                        {/* Ornamental flourishes */}
                                        <path d="M12,12 Q10,15 12,18 Q15,16 18,18 Q16,15 18,12 Q15,14 12,12" fill={frameColor} opacity="0.25" />
                                        <path d="M88,12 Q90,15 88,18 Q85,16 82,18 Q84,15 82,12 Q85,14 88,12" fill={frameColor} opacity="0.25" />
                                        <path d="M12,88 Q10,85 12,82 Q15,84 18,82 Q16,85 18,88 Q15,86 12,88" fill={frameColor} opacity="0.25" />
                                        <path d="M88,88 Q90,85 88,82 Q85,84 82,82 Q84,85 82,88 Q85,86 88,88" fill={frameColor} opacity="0.25" />

                                        {/* Decorative dots at midpoints */}
                                        <circle cx="50" cy="3" r="2" fill={frameColor} />
                                        <circle cx="50" cy="97" r="2" fill={frameColor} />
                                        <circle cx="3" cy="50" r="2" fill={frameColor} />
                                        <circle cx="97" cy="50" r="2" fill={frameColor} />

                                        {/* Small accent diamonds */}
                                        <path d="M25,3 L27,5 L25,7 L23,5 Z" fill={frameColor} />
                                        <path d="M75,3 L77,5 L75,7 L73,5 Z" fill={frameColor} />
                                        <path d="M25,97 L27,95 L25,93 L23,95 Z" fill={frameColor} />
                                        <path d="M75,97 L77,95 L75,93 L73,95 Z" fill={frameColor} />
                                    </>
                                ),

                                // Art Deco luxury frame with geometric patterns
                                art_deco_frame: (
                                    <>
                                        {/* Main borders */}
                                        <rect x="2" y="2" width="96" height="96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.3} />
                                        <rect x="5" y="5" width="90" height="90" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />

                                        {/* Art Deco corner blocks with fan pattern */}
                                        <path d="M5,5 L20,5 L20,20 L5,20 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                        <path d="M8,8 Q12.5,12.5 8,17 M10,8 Q12.5,12.5 10,17 M12,8 Q12.5,12.5 12,17 M14,8 Q12.5,12.5 14,17 M16,8 Q12.5,12.5 16,17" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.4} />

                                        <path d="M95,5 L80,5 L80,20 L95,20 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                        <path d="M92,8 Q87.5,12.5 92,17 M90,8 Q87.5,12.5 90,17 M88,8 Q87.5,12.5 88,17 M86,8 Q87.5,12.5 86,17 M84,8 Q87.5,12.5 84,17" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.4} />

                                        <path d="M5,95 L20,95 L20,80 L5,80 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                        <path d="M8,92 Q12.5,87.5 8,83 M10,92 Q12.5,87.5 10,83 M12,92 Q12.5,87.5 12,83 M14,92 Q12.5,87.5 14,83 M16,92 Q12.5,87.5 16,83" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.4} />

                                        <path d="M95,95 L80,95 L80,80 L95,80 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                        <path d="M92,92 Q87.5,87.5 92,83 M90,92 Q87.5,87.5 90,83 M88,92 Q87.5,87.5 88,83 M86,92 Q87.5,87.5 86,83 M84,92 Q87.5,87.5 84,83" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.4} />

                                        {/* Geometric accent lines */}
                                        <line x1="20" y1="5" x2="30" y2="15" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <line x1="80" y1="5" x2="70" y2="15" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <line x1="20" y1="95" x2="30" y2="85" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <line x1="80" y1="95" x2="70" y2="85" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    </>
                                ),

                                // Baroque royal frame with elaborate scrollwork
                                baroque_frame: (
                                    <>
                                        {/* Triple ornate borders */}
                                        <rect x="2" y="2" width="96" height="96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.4} />
                                        <rect x="4.5" y="4.5" width="91" height="91" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                        <rect x="7" y="7" width="86" height="86" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />

                                        {/* Baroque scrollwork corners */}
                                        <path d="M10,10 Q8,12 8,15 Q8,18 10,20 Q12,22 15,22 Q18,22 20,20 Q22,18 22,15 Q22,12 20,10 Q18,8 15,8 Q12,8 10,10" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                        <path d="M90,10 Q92,12 92,15 Q92,18 90,20 Q88,22 85,22 Q82,22 80,20 Q78,18 78,15 Q78,12 80,10 Q82,8 85,8 Q88,8 90,10" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                        <path d="M10,90 Q8,88 8,85 Q8,82 10,80 Q12,78 15,78 Q18,78 20,80 Q22,82 22,85 Q22,88 20,90 Q18,92 15,92 Q12,92 10,90" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                        <path d="M90,90 Q92,88 92,85 Q92,82 90,80 Q88,78 85,78 Q82,78 80,80 Q78,82 78,85 Q78,88 80,90 Q82,92 85,92 Q88,92 90,90" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />

                                        {/* Baroque flourishes */}
                                        <path d="M15,10 Q12,12 12,15 M15,10 Q18,12 18,15" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.5} />
                                        <path d="M85,10 Q88,12 88,15 M85,10 Q82,12 82,15" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.5} />
                                        <path d="M15,90 Q12,88 12,85 M15,90 Q18,88 18,85" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.5} />
                                        <path d="M85,90 Q88,88 88,85 M85,90 Q82,88 82,85" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.5} />

                                        {/* Side embellishments */}
                                        <path d="M35,3 Q40,5 45,3 M55,3 Q60,5 65,3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <path d="M35,97 Q40,95 45,97 M55,97 Q60,95 65,97" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <path d="M3,35 Q5,40 3,45 M3,55 Q5,60 3,65" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <path d="M97,35 Q95,40 97,45 M97,55 Q95,60 97,65" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    </>
                                ),

                                // Modern luxury minimalist frame
                                modern_frame: (
                                    <>
                                        {/* Clean outer border */}
                                        <rect x="4" y="4" width="92" height="92" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.5} />

                                        {/* Corner L-shapes */}
                                        <line x1="4" y1="20" x2="4" y2="4" stroke={frameColor} strokeWidth={frameThickness * 2.5} />
                                        <line x1="4" y1="4" x2="20" y2="4" stroke={frameColor} strokeWidth={frameThickness * 2.5} />

                                        <line x1="96" y1="20" x2="96" y2="4" stroke={frameColor} strokeWidth={frameThickness * 2.5} />
                                        <line x1="96" y1="4" x2="80" y2="4" stroke={frameColor} strokeWidth={frameThickness * 2.5} />

                                        <line x1="4" y1="80" x2="4" y2="96" stroke={frameColor} strokeWidth={frameThickness * 2.5} />
                                        <line x1="4" y1="96" x2="20" y2="96" stroke={frameColor} strokeWidth={frameThickness * 2.5} />

                                        <line x1="96" y1="80" x2="96" y2="96" stroke={frameColor} strokeWidth={frameThickness * 2.5} />
                                        <line x1="96" y1="96" x2="80" y2="96" stroke={frameColor} strokeWidth={frameThickness * 2.5} />

                                        {/* Minimalist accent dots */}
                                        <circle cx="50" cy="6" r="1.5" fill={frameColor} />
                                        <circle cx="50" cy="94" r="1.5" fill={frameColor} />
                                        <circle cx="6" cy="50" r="1.5" fill={frameColor} />
                                        <circle cx="94" cy="50" r="1.5" fill={frameColor} />
                                    </>
                                ),

                                // Islamic geometric pattern frame
                                islamic_frame: (
                                    <>
                                        {/* Main border */}
                                        <rect x="2" y="2" width="96" height="96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.2} />
                                        <rect x="5" y="5" width="90" height="90" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />

                                        {/* Islamic 8-point stars in corners */}
                                        <path d="M10,12 L12,10 L14,12 L12,14 Z M12,8 L14,10 L12,12 L10,10 Z M16,10 L14,12 L12,10 L14,8 Z M14,14 L12,12 L14,10 L16,12 Z"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="12" cy="10" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />

                                        <path d="M90,12 L88,10 L86,12 L88,14 Z M88,8 L86,10 L88,12 L90,10 Z M84,10 L86,12 L88,10 L86,8 Z M86,14 L88,12 L86,10 L84,12 Z"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="88" cy="10" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />

                                        <path d="M10,88 L12,90 L14,88 L12,86 Z M12,92 L14,90 L12,88 L10,90 Z M16,90 L14,88 L12,90 L14,92 Z M14,86 L12,88 L14,90 L16,88 Z"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="12" cy="90" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />

                                        <path d="M90,88 L88,90 L86,88 L88,86 Z M88,92 L86,90 L88,88 L90,90 Z M84,90 L86,88 L88,90 L86,92 Z M86,86 L88,88 L86,90 L84,88 Z"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="88" cy="90" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />

                                        {/* Geometric interlace pattern */}
                                        <rect x="7.5" y="7.5" width="85" height="85" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} strokeDasharray="4,2" />

                                        {/* Arabesque motifs at midpoints */}
                                        <path d="M48,3 L50,5 L52,3 L50,1 Z" fill={frameColor} />
                                        <path d="M48,97 L50,95 L52,97 L50,99 Z" fill={frameColor} />
                                        <path d="M3,48 L5,50 L3,52 L1,50 Z" fill={frameColor} />
                                        <path d="M97,48 L95,50 L97,52 L99,50 Z" fill={frameColor} />
                                    </>
                                ),

                                // Modern Islamic - clean geometry with subtle star motifs
                                modern_islamic: (
                                    <>
                                        <rect x="3" y="3" width="94" height="94" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.1} />
                                        <rect x="6" y="6" width="88" height="88" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} strokeDasharray="6,3" />
                                        <path d="M10,10 L15,6 L20,10 L15,14 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <path d="M90,10 L94,15 L90,20 L86,15 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <path d="M10,90 L6,85 L10,80 L14,85 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <path d="M90,90 L85,94 L80,90 L85,86 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <circle cx="50" cy="4" r="2" fill={frameColor} />
                                        <circle cx="50" cy="96" r="2" fill={frameColor} />
                                        <circle cx="4" cy="50" r="2" fill={frameColor} />
                                        <circle cx="96" cy="50" r="2" fill={frameColor} />
                                    </>
                                ),

                                // Classic Islamic - ornate eight-point star accents
                                classic_islamic: (
                                    <>
                                        <rect x="2" y="2" width="96" height="96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.4} />
                                        <rect x="5" y="5" width="90" height="90" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                        <path d="M12,12 L16,10 L20,12 L18,16 L20,20 L16,18 L12,20 L14,16 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <path d="M88,12 L90,16 L88,20 L84,18 L80,20 L82,16 L80,12 L84,14 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <path d="M12,88 L14,84 L12,80 L16,82 L20,80 L18,84 L20,88 L16,86 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <path d="M88,88 L84,86 L80,88 L82,84 L80,80 L84,82 L88,80 L86,84 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <path d="M50,3 L52,6 L50,9 L48,6 Z" fill={frameColor} />
                                        <path d="M50,97 L52,94 L50,91 L48,94 Z" fill={frameColor} />
                                        <path d="M3,50 L6,52 L9,50 L6,48 Z" fill={frameColor} />
                                        <path d="M97,50 L94,52 L91,50 L94,48 Z" fill={frameColor} />
                                    </>
                                ),

                                // Modern Ornate - elaborate scrollwork frame like the certificate image
                                modern_ornate: (
                                    <>
                                        {/* Triple border system */}
                                        <rect x="1.5" y="1.5" width="97" height="97" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.8} />
                                        <rect x="3.5" y="3.5" width="93" height="93" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.2} />
                                        <rect x="6" y="6" width="88" height="88" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />

                                        {/* Continuous scrollwork on top border */}
                                        <path d="M8,4 Q10,2 12,4 Q14,6 16,4 Q18,2 20,4 Q22,6 24,4 Q26,2 28,4 Q30,6 32,4 Q34,2 36,4 Q38,6 40,4 Q42,2 44,4 Q46,6 48,4 Q50,2 52,4 Q54,6 56,4 Q58,2 60,4 Q62,6 64,4 Q66,2 68,4 Q70,6 72,4 Q74,2 76,4 Q78,6 80,4 Q82,2 84,4 Q86,6 88,4 Q90,2 92,4"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M10,2.5 Q12,1 14,2.5 M18,2.5 Q20,1 22,2.5 M26,2.5 Q28,1 30,2.5 M34,2.5 Q36,1 38,2.5 M42,2.5 Q44,1 46,2.5 M50,2.5 Q52,1 54,2.5 M58,2.5 Q60,1 62,2.5 M66,2.5 Q68,1 70,2.5 M74,2.5 Q76,1 78,2.5 M82,2.5 Q84,1 86,2.5 M90,2.5 Q92,1 94,2.5"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />

                                        {/* Bottom scrollwork */}
                                        <path d="M8,96 Q10,98 12,96 Q14,94 16,96 Q18,98 20,96 Q22,94 24,96 Q26,98 28,96 Q30,94 32,96 Q34,98 36,96 Q38,94 40,96 Q42,98 44,96 Q46,94 48,96 Q50,98 52,96 Q54,94 56,96 Q58,98 60,96 Q62,94 64,96 Q66,98 68,96 Q70,94 72,96 Q74,98 76,96 Q78,94 80,96 Q82,98 84,96 Q86,94 88,96 Q90,98 92,96"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M10,97.5 Q12,99 14,97.5 M18,97.5 Q20,99 22,97.5 M26,97.5 Q28,99 30,97.5 M34,97.5 Q36,99 38,97.5 M42,97.5 Q44,99 46,97.5 M50,97.5 Q52,99 54,97.5 M58,97.5 Q60,99 62,97.5 M66,97.5 Q68,99 70,97.5 M74,97.5 Q76,99 78,97.5 M82,97.5 Q84,99 86,97.5 M90,97.5 Q92,99 94,97.5"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />

                                        {/* Left side scrollwork */}
                                        <path d="M4,8 Q2,10 4,12 Q6,14 4,16 Q2,18 4,20 Q6,22 4,24 Q2,26 4,28 Q6,30 4,32 Q2,34 4,36 Q6,38 4,40 Q2,42 4,44 Q6,46 4,48 Q2,50 4,52 Q6,54 4,56 Q2,58 4,60 Q6,62 4,64 Q2,66 4,68 Q6,70 4,72 Q2,74 4,76 Q6,78 4,80 Q2,82 4,84 Q6,86 4,88 Q2,90 4,92"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M2.5,10 Q1,12 2.5,14 M2.5,18 Q1,20 2.5,22 M2.5,26 Q1,28 2.5,30 M2.5,34 Q1,36 2.5,38 M2.5,42 Q1,44 2.5,46 M2.5,50 Q1,52 2.5,54 M2.5,58 Q1,60 2.5,62 M2.5,66 Q1,68 2.5,70 M2.5,74 Q1,76 2.5,78 M2.5,82 Q1,84 2.5,86 M2.5,90 Q1,92 2.5,94"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />

                                        {/* Right side scrollwork */}
                                        <path d="M96,8 Q98,10 96,12 Q94,14 96,16 Q98,18 96,20 Q94,22 96,24 Q98,26 96,28 Q94,30 96,32 Q98,34 96,36 Q94,38 96,40 Q98,42 96,44 Q94,46 96,48 Q98,50 96,52 Q94,54 96,56 Q98,58 96,60 Q94,62 96,64 Q98,66 96,68 Q94,70 96,72 Q98,74 96,76 Q94,78 96,80 Q98,82 96,84 Q94,86 96,88 Q98,90 96,92"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M97.5,10 Q99,12 97.5,14 M97.5,18 Q99,20 97.5,22 M97.5,26 Q99,28 97.5,30 M97.5,34 Q99,36 97.5,38 M97.5,42 Q99,44 97.5,46 M97.5,50 Q99,52 97.5,54 M97.5,58 Q99,60 97.5,62 M97.5,66 Q99,68 97.5,70 M97.5,74 Q99,76 97.5,78 M97.5,82 Q99,84 97.5,86 M97.5,90 Q99,92 97.5,94"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />

                                        {/* Ornate corner flourishes */}
                                        <path d="M8,8 Q6,10 8,12 Q10,14 12,12 Q14,10 12,8 Q10,6 8,8 M8,8 Q10,6 12,8 M8,8 Q6,10 8,12 M12,8 Q14,10 12,12"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                        <circle cx="10" cy="10" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="10" cy="10" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.3} />

                                        <path d="M92,8 Q94,10 92,12 Q90,14 88,12 Q86,10 88,8 Q90,6 92,8 M92,8 Q90,6 88,8 M92,8 Q94,10 92,12 M88,8 Q86,10 88,12"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                        <circle cx="90" cy="10" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="90" cy="10" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.3} />

                                        <path d="M8,92 Q6,90 8,88 Q10,86 12,88 Q14,90 12,92 Q10,94 8,92 M8,92 Q10,94 12,92 M8,92 Q6,90 8,88 M12,92 Q14,90 12,88"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                        <circle cx="10" cy="90" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="10" cy="90" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.3} />

                                        <path d="M92,92 Q94,90 92,88 Q90,86 88,88 Q86,90 88,92 Q90,94 92,92 M92,92 Q90,94 88,92 M92,92 Q94,90 92,88 M88,92 Q86,90 88,88"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                        <circle cx="90" cy="90" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="90" cy="90" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.3} />

                                        {/* Decorative swirls in corner areas */}
                                        <path d="M15,8 Q18,6 20,8 Q22,10 20,13 Q18,15 15,13 Q13,10 15,8" fill={frameColor} opacity="0.2" />
                                        <path d="M85,8 Q82,6 80,8 Q78,10 80,13 Q82,15 85,13 Q87,10 85,8" fill={frameColor} opacity="0.2" />
                                        <path d="M15,92 Q18,94 20,92 Q22,90 20,87 Q18,85 15,87 Q13,90 15,92" fill={frameColor} opacity="0.2" />
                                        <path d="M85,92 Q82,94 80,92 Q78,90 80,87 Q82,85 85,87 Q87,90 85,92" fill={frameColor} opacity="0.2" />

                                        {/* Center ornamental elements */}
                                        <circle cx="50" cy="3" r="2" fill={frameColor} />
                                        <circle cx="50" cy="97" r="2" fill={frameColor} />
                                        <circle cx="3" cy="50" r="2" fill={frameColor} />
                                        <circle cx="97" cy="50" r="2" fill={frameColor} />

                                        {/* Additional decorative flourishes */}
                                        <path d="M25,5 Q27,3 29,5 M71,5 Q73,3 75,5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M25,95 Q27,97 29,95 M71,95 Q73,97 75,95" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M5,25 Q3,27 5,29 M5,71 Q3,73 5,75" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M95,25 Q97,27 95,29 M95,71 Q97,73 95,75" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    </>
                                )
                            };

                            return (
                                <svg
                                    viewBox="0 0 100 100"
                                    preserveAspectRatio="none"
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    {framePatterns[frameStyle] || framePatterns.vintage_border}
                                </svg>
                            );
                        })()}

                        {/* Border Pattern Overlay */}
                        {(() => {
                            const borderPattern = livePreviewData?.borderPattern || form.getFieldValue('borderPattern') || 'none';
                            const borderColor = livePreviewData?.borderColor || form.getFieldValue('borderColor') || '#000000';

                            if (borderPattern === 'none') return null;

                            const patterns = {
                                dots: 'M0,5 L0,5 M10,5 L10,5 M20,5 L20,5 M30,5 L30,5 M40,5 L40,5 M50,5 L50,5 M60,5 L60,5 M70,5 L70,5 M80,5 L80,5 M90,5 L90,5 M100,5 L100,5',
                                waves: 'M0,5 Q5,0 10,5 T20,5 T30,5 T40,5 T50,5 T60,5 T70,5 T80,5 T90,5 T100,5',
                                zigzag: 'M0,8 L5,2 L10,8 L15,2 L20,8 L25,2 L30,8 L35,2 L40,8 L45,2 L50,8 L55,2 L60,8 L65,2 L70,8 L75,2 L80,8 L85,2 L90,8 L95,2 L100,8',
                                chain: 'M0,5 Q5,0 10,5 Q15,10 20,5 Q25,0 30,5 Q35,10 40,5 Q45,0 50,5 Q55,10 60,5 Q65,0 70,5 Q75,10 80,5 Q85,0 90,5 Q95,10 100,5'
                            };

                            return (
                                <>
                                    {/* Top border pattern */}
                                    <svg viewBox="0 0 100 10" preserveAspectRatio="none" style={{ position: 'absolute', top: '5px', left: '5px', width: 'calc(100% - 10px)', height: '15px' }}>
                                        <path d={patterns[borderPattern]} fill="none" stroke={borderColor} strokeWidth="1.5" />
                                    </svg>
                                    {/* Bottom border pattern */}
                                    <svg viewBox="0 0 100 10" preserveAspectRatio="none" style={{ position: 'absolute', bottom: '5px', left: '5px', width: 'calc(100% - 10px)', height: '15px' }}>
                                        <path d={patterns[borderPattern]} fill="none" stroke={borderColor} strokeWidth="1.5" />
                                    </svg>
                                    {/* Left border pattern */}
                                    <svg viewBox="0 0 10 100" preserveAspectRatio="none" style={{ position: 'absolute', top: '5px', left: '5px', width: '15px', height: 'calc(100% - 10px)' }}>
                                        <path d={patterns[borderPattern].replace(/M0,5/g, 'M5,0').replace(/L(\d+),5/g, 'L5,$1').replace(/Q(\d+),(\d+)/g, 'Q$2,$1').replace(/T(\d+),5/g, 'T5,$1')} fill="none" stroke={borderColor} strokeWidth="1.5" />
                                    </svg>
                                    {/* Right border pattern */}
                                    <svg viewBox="0 0 10 100" preserveAspectRatio="none" style={{ position: 'absolute', top: '5px', right: '5px', width: '15px', height: 'calc(100% - 10px)' }}>
                                        <path d={patterns[borderPattern].replace(/M0,5/g, 'M5,0').replace(/L(\d+),5/g, 'L5,$1').replace(/Q(\d+),(\d+)/g, 'Q$2,$1').replace(/T(\d+),5/g, 'T5,$1')} fill="none" stroke={borderColor} strokeWidth="1.5" />
                                    </svg>
                                </>
                            );
                        })()}

                        {/* Corner Decorations */}
                        {(livePreviewData?.cornerDecorationEnabled || form.getFieldValue('cornerDecorationEnabled')) && (() => {
                            const decorationType = livePreviewData?.cornerDecorationType || form.getFieldValue('cornerDecorationType') || 'floral';
                            const decorationSize = livePreviewData?.cornerDecorationSize || form.getFieldValue('cornerDecorationSize') || 50;
                            const decorationColor = livePreviewData?.cornerDecorationColor || form.getFieldValue('cornerDecorationColor') || '#DAA520';

                            const decorations = {
                                floral: 'M0,0 L0,65 Q5,58 10,53 L22,41 Q28,35 35,35 Q40,35 44,39 L50,45 M0,18 Q10,16 17,21 Q22,25 25,32 Q27,38 32,42 L40,48 M12,0 Q12,14 20,22 Q26,28 34,31 Q41,34 47,38 L55,45 M0,36 Q14,34 22,40 Q28,45 30,53 L34,62 M30,0 Q27,18 36,28 Q42,35 50,38 L58,42 M5,5 Q8,8 8,12 Q8,16 5,19 Q2,22 -2,22 M15,15 Q19,18 19,23 Q19,28 15,31 M25,25 Q30,28 30,34 Q30,40 25,43 M35,35 Q41,38 41,45 M45,5 L50,10 L47,13 L42,8 M55,15 L60,20 L58,22 L53,17',
                                geometric: 'M0,0 L0,80 L5,75 L5,5 L75,5 L80,0 M10,10 L10,70 L15,65 L15,15 L65,15 L70,10 M20,20 L20,60 L25,55 L25,25 L55,25 L60,20 M30,30 L30,50 L35,45 L35,35 L45,35 L50,30 M0,15 L15,0 M0,30 L30,0 M0,45 L45,0 M0,60 L60,0 M0,75 L75,0',
                                ornamental: 'M0,0 L0,70 Q5,60 10,55 L25,40 Q30,35 35,35 Q40,35 45,40 L55,50 M0,20 Q8,18 15,22 Q20,25 23,30 Q25,35 30,38 L40,45 Q45,48 50,48 M10,0 Q10,15 18,23 Q23,28 30,30 Q38,32 43,37 L53,47 M25,0 Q22,20 32,30 Q38,36 47,38 Q55,40 60,45 L70,55 M0,40 Q12,38 20,43 Q26,47 28,55 L32,65 M40,0 Q35,25 45,38 Q52,47 62,50 L72,53',
                                islamic: 'M0,0 L0,80 Q5,70 12,63 L28,47 Q35,40 42,40 L50,40 M0,20 Q10,18 18,24 Q24,29 28,36 Q32,42 38,46 L48,54 M10,0 Q10,18 20,28 Q26,34 35,37 Q43,40 50,45 L60,55 M0,40 Q15,38 25,45 Q32,50 35,58 L40,68 M30,0 Q27,22 38,35 Q45,43 55,47 L65,52 M0,60 Q20,58 30,65 L38,73 M50,0 Q45,30 58,45 L68,55 M20,0 L30,10 L25,15 L15,5 M40,0 L50,10 L48,12 L38,2 M60,0 L70,10 L68,12 L58,2',
                                arabesque: 'M0,0 L0,60 Q0,50 8,42 Q15,35 25,32 Q35,29 42,35 L50,43 M0,15 Q10,15 18,22 Q25,28 28,38 Q30,45 35,50 L45,60 M0,30 Q15,28 25,35 Q32,40 35,50 Q37,58 42,65 L50,73 M15,0 Q15,12 22,20 Q28,27 38,30 Q48,32 55,38 L65,48 M30,0 Q28,15 35,25 Q40,32 50,35 Q58,37 65,42 L73,50 M45,0 Q42,18 50,28 Q55,35 65,40 L75,48',
                                baroque: 'M0,0 L0,75 Q3,68 8,63 Q15,56 23,52 Q30,49 38,50 Q45,51 50,56 L58,64 M0,18 Q8,16 15,20 Q21,24 25,30 Q28,36 33,40 Q38,44 44,46 L52,49 M12,0 Q12,12 18,20 Q23,27 30,31 Q37,35 44,37 Q51,39 57,43 L65,50 M0,35 Q10,33 18,38 Q24,42 27,49 Q29,55 33,60 L40,68 M28,0 Q26,15 33,25 Q38,33 46,38 Q53,42 60,45 L68,49 M0,52 Q14,50 22,56 Q28,61 30,68 L33,76 M45,0 Q42,20 50,32 Q56,41 65,46 L73,50 M15,0 L22,7 Q25,10 25,15 L25,22 M35,0 L42,7 Q48,13 52,20 L58,30 M55,0 L62,7 Q68,13 72,22 L76,32',
                                celtic: 'M0,0 L0,80 Q8,72 16,68 L32,60 Q40,56 48,60 L56,64 M0,24 Q12,20 20,26 Q26,31 28,40 Q30,48 36,54 L44,60 M16,0 Q16,16 24,28 Q30,36 40,40 Q48,44 56,50 L64,56 M0,48 Q16,44 26,52 Q32,58 34,68 L38,78 M40,0 Q36,24 46,38 Q52,48 62,54 L72,60 M8,8 L16,16 L12,20 L4,12 M24,24 L32,32 L28,36 L20,28 M40,40 L48,48 L44,52 L36,44 M56,8 L64,16 L60,20 L52,12 M12,0 Q10,10 16,16 M28,0 Q24,12 32,20 M44,0 Q38,14 48,24 M60,0 Q52,16 64,28',
                                art_deco: 'M0,0 L0,80 L3,80 L3,3 L80,3 L80,0 M8,8 L8,72 L10,72 L10,10 L72,10 L72,8 M15,15 L15,65 L17,65 L17,17 L65,17 L65,15 M22,22 L22,58 L24,58 L24,24 L58,24 L58,22 M29,29 L29,51 L31,51 L31,31 L51,31 L51,29 M36,36 L36,44 L38,44 L38,38 L44,38 L44,36 M0,12 L12,0 M0,25 L25,0 M0,38 L38,0 M0,51 L51,0 M0,64 L64,0 M0,77 L77,0',
                                vintage: 'M0,0 L0,72 Q6,64 14,58 L28,44 Q36,36 44,38 L52,40 M0,20 Q10,18 18,24 Q24,29 27,36 Q30,43 36,48 L44,54 M14,0 Q14,18 24,28 Q30,34 39,37 Q47,40 54,45 L62,52 M0,40 Q16,38 24,46 Q30,52 32,60 L36,70 M34,0 Q30,26 42,38 Q48,46 58,50 L68,54 M8,8 Q12,12 12,18 Q12,24 8,28 Q4,32 0,32 M18,18 Q24,22 24,30 Q24,38 18,42 M28,28 Q35,32 35,40 M38,8 L44,14 L41,17 L35,11 M48,18 L54,24 L52,26 L46,20 M58,28 L64,34 L62,36 L56,30',
                                modern_curves: 'M0,0 L0,80 M0,15 L65,15 M0,30 L50,30 M0,45 L35,45 M0,60 L20,60 M15,0 L15,65 M30,0 L30,50 M45,0 L45,35 M60,0 L60,20 M0,0 L80,80 M10,0 L70,60 M20,0 L60,40 M30,0 L50,20 M40,0 L40,0',
                                elegant_swirl: 'M0,0 L0,60 Q0,50 8,42 Q15,35 25,32 Q35,29 42,35 L50,43 M0,15 Q10,15 18,22 Q25,28 28,38 Q30,45 35,50 L45,60 M0,30 Q15,28 25,35 Q32,40 35,50 Q37,58 42,65 L50,73 M15,0 Q15,12 22,20 Q28,27 38,30 Q48,32 55,38 L65,48 M30,0 Q28,15 35,25 Q40,32 50,35 Q58,37 65,42 L73,50 M45,0 Q42,18 50,28 Q55,35 65,40 L75,48',
                                royal: 'M0,0 L0,70 Q5,62 12,55 L30,37 Q38,29 47,32 L55,35 M0,22 Q12,20 22,27 Q28,32 32,40 Q35,47 42,52 L52,60 M15,0 Q15,20 27,32 Q34,39 44,42 Q53,45 60,50 L70,58 M0,45 Q18,43 28,52 Q34,58 37,67 L42,77 M38,0 Q34,28 47,42 Q55,50 66,54 L76,58 M18,0 L28,10 Q32,14 32,20 L32,30 M40,0 L50,10 Q56,16 60,24 L66,36 M10,0 L15,5 L12,8 L7,3 M25,0 L32,7 L29,10 L22,3 M55,0 L62,7 L60,9 L53,2'
                            };

                            const path = decorations[decorationType] || decorations.floral;

                            return (
                                <>
                                    {/* Top Left */}
                                    <svg viewBox="0 0 100 100" style={{ position: 'absolute', top: '10px', left: '10px', width: `${decorationSize}px`, height: `${decorationSize}px` }}>
                                        <path d={path} fill={decorationColor} />
                                    </svg>
                                    {/* Top Right */}
                                    <svg viewBox="0 0 100 100" style={{ position: 'absolute', top: '10px', right: '10px', width: `${decorationSize}px`, height: `${decorationSize}px`, transform: 'scaleX(-1)' }}>
                                        <path d={path} fill={decorationColor} />
                                    </svg>
                                    {/* Bottom Left */}
                                    <svg viewBox="0 0 100 100" style={{ position: 'absolute', bottom: '10px', left: '10px', width: `${decorationSize}px`, height: `${decorationSize}px`, transform: 'scaleY(-1)' }}>
                                        <path d={path} fill={decorationColor} />
                                    </svg>
                                    {/* Bottom Right */}
                                    <svg viewBox="0 0 100 100" style={{ position: 'absolute', bottom: '10px', right: '10px', width: `${decorationSize}px`, height: `${decorationSize}px`, transform: 'scale(-1)' }}>
                                        <path d={path} fill={decorationColor} />
                                    </svg>
                                </>
                            );
                        })()}

                        {/* Header Logo */}
                        {(!removeLogo && (logoFile || editingTemplate?.header?.logo)) && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: (() => {
                                        const align = livePreviewData?.logoAlign || form.getFieldValue('logoAlign') || 'center';
                                        if (align === 'left') return 'flex-start';
                                        if (align === 'right') return 'flex-end';
                                        return 'center';
                                    })(),
                                    marginBottom: `${livePreviewData?.headerSpacing || form.getFieldValue('headerSpacing') || 30}px`,
                                    marginTop: `${livePreviewData?.logoOffsetTop || form.getFieldValue('logoOffsetTop') || 0}px`,
                                    position: 'relative',
                                    transform: `translate(${getOffsetValue('logo', 'x')}px, ${getOffsetValue('logo', 'y')}px)`,
                                    cursor: previewEditMode ? 'move' : 'default'
                                }}
                                onMouseDown={(event) => handleDragStart('logo', event)}
                            >
                                <img
                                    src={logoFile ? URL.createObjectURL(logoFile) : `${config.baseUrl}${editingTemplate.header?.logo}`}
                                    alt="Logo"
                                    style={{
                                        maxWidth: `${livePreviewData?.logoMaxWidth || form.getFieldValue('logoMaxWidth') || 150}px`,
                                        maxHeight: `${livePreviewData?.logoMaxHeight || form.getFieldValue('logoMaxHeight') || 100}px`,
                                        objectFit: 'contain'
                                    }}
                                />
                            </div>
                        )}

                        {/* Header */}
                        <h1
                            style={{
                                fontFamily: livePreviewData?.instituteNameFontFamily || form.getFieldValue('instituteNameFontFamily') || 'Arial',
                                fontSize: `${livePreviewData?.instituteNameFontSize || form.getFieldValue('instituteNameFontSize') || 24}px`,
                                color: livePreviewData?.instituteNameFontColor || form.getFieldValue('instituteNameFontColor') || '#000000',
                                textAlign: livePreviewData?.headerAlign || form.getFieldValue('headerAlign') || 'center',
                                marginBottom: `${livePreviewData?.headerSpacing || form.getFieldValue('headerSpacing') || 30}px`,
                                position: 'relative',
                                transform: `translate(${getOffsetValue('header', 'x')}px, ${getOffsetValue('header', 'y')}px)`,
                                cursor: previewEditMode ? 'move' : 'default'
                            }}
                            onMouseDown={(event) => handleDragStart('header', event)}
                        >
                            {livePreviewData?.instituteName || form.getFieldValue('instituteName') || 'Institute Name'}
                        </h1>

                        {/* Title */}
                        <h2
                            style={{
                                fontFamily: livePreviewData?.titleFontFamily || form.getFieldValue('titleFontFamily') || 'Arial',
                                fontSize: `${livePreviewData?.titleFontSize || form.getFieldValue('titleFontSize') || 36}px`,
                                color: livePreviewData?.titleFontColor || form.getFieldValue('titleFontColor') || '#000000',
                                textAlign: livePreviewData?.titleAlign || form.getFieldValue('titleAlign') || 'center',
                                marginBottom: `${livePreviewData?.titleSpacing || form.getFieldValue('titleSpacing') || 20}px`,
                                position: 'relative',
                                transform: `translate(${getOffsetValue('title', 'x')}px, ${getOffsetValue('title', 'y')}px)`,
                                cursor: previewEditMode ? 'move' : 'default'
                            }}
                            onMouseDown={(event) => handleDragStart('title', event)}
                        >
                            {livePreviewData?.titleText || form.getFieldValue('titleText') || 'Certificate of Completion'}
                        </h2>

                        {/* Body */}
                        <div
                            style={{
                                fontFamily: livePreviewData?.bodyFontFamily || form.getFieldValue('bodyFontFamily') || 'Arial',
                                fontSize: `${livePreviewData?.bodyFontSize || form.getFieldValue('bodyFontSize') || 16}px`,
                                fontWeight: livePreviewData?.bodyFontWeight || form.getFieldValue('bodyFontWeight') || 'normal',
                                color: livePreviewData?.bodyFontColor || form.getFieldValue('bodyFontColor') || '#333333',
                                textAlign: livePreviewData?.bodyAlign || form.getFieldValue('bodyAlign') || 'center',
                                maxWidth: `${livePreviewData?.bodyMaxWidth || form.getFieldValue('bodyMaxWidth') || 600}px`,
                                margin: `0 auto ${livePreviewData?.bodySpacing || form.getFieldValue('bodySpacing') || 30}px`,
                                position: 'relative',
                                transform: `translate(${getOffsetValue('body', 'x')}px, ${getOffsetValue('body', 'y')}px)`,
                                cursor: previewEditMode ? 'move' : 'default',
                                whiteSpace: 'pre-wrap'
                            }}
                            onMouseDown={(event) => handleDragStart('body', event)}
                        >
                            {(() => {
                                const template = (livePreviewData?.bodyTemplate || form.getFieldValue('bodyTemplate')) || 'This is to certify that {{studentName}} has successfully completed the {{courseName}} course.';
                                const sampleData = {
                                    '{{studentName}}': 'John Doe',
                                    '{{courseName}}': 'Sample Course',
                                    '{{completionDate}}': new Date().toLocaleDateString(),
                                    '{{issueDate}}': new Date().toLocaleDateString(),
                                    '{{certificateNumber}}': 'CERT-001',
                                    '{{grade}}': 'A+',
                                    '{{duration}}': '3 months',
                                    '{{instructorName}}': 'Jane Smith',
                                    '{{academyName}}': 'Academy Name'
                                };

                                // Split text by placeholders while keeping them
                                const parts = template.split(/(\{\{[^}]+\}\})/g);

                                return parts.map((part, index) => {
                                    if (part.match(/\{\{[^}]+\}\}/)) {
                                        const style = placeholderStyles[part] || {};
                                        return (
                                            <span
                                                key={index}
                                                style={{
                                                    fontFamily: style.fontFamily,
                                                    fontSize: style.fontSize ? `${style.fontSize}px` : undefined,
                                                    color: style.color,
                                                    fontWeight: style.fontWeight || 'normal',
                                                    fontStyle: style.fontStyle || 'normal',
                                                    textDecoration: style.textDecoration || 'none',
                                                    textTransform: style.textTransform || 'none'
                                                }}
                                            >
                                                {sampleData[part] || part}
                                            </span>
                                        );
                                    }
                                    // Render HTML tags in regular text
                                    return <span key={index} dangerouslySetInnerHTML={{ __html: part }} />;
                                });
                            })()}
                        </div>

                        {/* Badges */}
                        {badges.map((badge, index) => (
                            (badge.file || badge.url) && (
                                <div
                                    key={index}
                                    style={{
                                        position: 'absolute',
                                        left: `${badge.x || 50}%`,
                                        top: `${badge.y || 50}%`,
                                        transform: `translate(-50%, -50%) translate(${getOffsetValue(`badge${index}`, 'x')}px, ${getOffsetValue(`badge${index}`, 'y')}px)`,
                                        cursor: previewEditMode ? 'move' : 'default',
                                        zIndex: 10
                                    }}
                                    onMouseDown={(event) => handleDragStart(`badge${index}`, event)}
                                >
                                    <img
                                        src={badge.file ? URL.createObjectURL(badge.file) : `${config.baseUrl}${badge.url}`}
                                        alt={`Badge ${index + 1}`}
                                        style={{
                                            width: `${badge.size || 80}px`,
                                            height: `${badge.size || 80}px`,
                                            objectFit: 'contain'
                                        }}
                                    />
                                </div>
                            )
                        ))}

                        {/* Watermark */}
                        {(watermarkFile || editingTemplate?.watermark?.image) && !removeWatermark && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    opacity: form.getFieldValue('watermarkOpacity') || editingTemplate?.watermark?.opacity || 0.1,
                                    zIndex: 1,
                                    pointerEvents: 'none'
                                }}
                            >
                                <img
                                    src={watermarkFile ? URL.createObjectURL(watermarkFile) : `${config.baseUrl}${editingTemplate.watermark.image}`}
                                    alt="Watermark"
                                    style={{
                                        width: `${form.getFieldValue('watermarkSize') || editingTemplate?.watermark?.size || 300}px`,
                                        height: 'auto',
                                        objectFit: 'contain'
                                    }}
                                />
                            </div>
                        )}


                        {/* Signature Slots Preview */}
                        <div style={{
                            position: 'absolute',
                            bottom: '15mm',
                            left: '15mm',
                            right: '15mm',
                            transform: `translate(${getOffsetValue('footer', 'x')}px, ${getOffsetValue('footer', 'y')}px)`
                        }}>
                            {signatureSlots.length > 0 && (
                                <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-around' }}>
                                    {signatureSlots.map((slot, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                textAlign: 'center',
                                                flex: slot.position === 'center' ? '0 0 auto' : '1',
                                                maxWidth: '200px'
                                            }}
                                        >
                                            <div style={{ borderTop: '1px solid #000', paddingTop: '5px' }}>
                                                {slot.showSignerName && slot.signerName && (
                                                    <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '3px' }}>
                                                        <Text>{slot.signerName}</Text>
                                                    </div>
                                                )}
                                                {slot.showLabel && <Text>{slot.label}</Text>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {/* Footer Showing Date LivePreview */}
                            {(livePreviewData?.showDate || form.getFieldValue('showDate')) && (
                                <div style={{ textAlign: 'center', width: '100%' }}>
                                    <div style={{ fontSize: '14px', marginBottom: '5px' }}>
                                        {livePreviewData?.issueDate || form.getFieldValue('issueDate') || new Date().toLocaleDateString()}
                                    </div>

                                </div>
                            )}
                        </div>
                    </div>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={createMutation.isLoading || updateMutation.isLoading}>
                                {editingTemplate ? t('update') : t('create')}
                            </Button>
                            <Button onClick={() => {
                                setIsModalVisible(false);
                                setEditingTemplate(null);
                                form.resetFields();
                            }}>
                                {t('cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Preview Modal */}
            <Modal
                title={t('previewTemplate')}
                open={isPreviewVisible}
                onCancel={() => setIsPreviewVisible(false)}
                footer={null}
                width="95%"
                style={{ top: 20 }}
            >
                {previewTemplate && (
                    <div
                        style={{
                            width: previewTemplate.design?.orientation === 'landscape' ? '297mm' : '210mm',
                            height: previewTemplate.design?.orientation === 'landscape' ? '210mm' : '297mm',
                            margin: '0 auto',
                            backgroundColor: previewTemplate.design?.backgroundColor,
                            backgroundImage: (() => {
                                if (previewTemplate.canvaImport?.enabled && previewTemplate.canvaImport?.originalFile) {
                                    return `url(${config.baseUrl}${previewTemplate.canvaImport.originalFile})`;
                                }
                                if (previewTemplate.design?.backgroundImage) {
                                    return `url(${config.baseUrl}${previewTemplate.design.backgroundImage})`;
                                }
                                return 'none';
                            })(),
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            padding: `${previewTemplate.design?.layoutSettings?.contentPadding || 30}px`,
                            textAlign: 'left',
                            position: 'relative',
                            boxSizing: 'border-box',
                            boxShadow: (() => {
                                const borderStyle = previewTemplate.design?.borderStyle || 'single';
                                const borderColor = previewTemplate.design?.borderColor || '#000000';
                                const secondaryColor = previewTemplate.design?.secondaryBorderColor || '#DAA520';

                                if (borderStyle === 'none') return 'none';
                                if (borderStyle === 'single') return `inset 0 0 0 2px ${borderColor}`;
                                if (borderStyle === 'double') return `inset 0 0 0 2px ${borderColor}, inset 0 0 0 6px ${secondaryColor}, inset 0 0 0 8px ${borderColor}`;
                                if (borderStyle === 'triple') return `inset 0 0 0 2px ${borderColor}, inset 0 0 0 5px ${secondaryColor}, inset 0 0 0 7px ${borderColor}, inset 0 0 0 10px ${secondaryColor}, inset 0 0 0 12px ${borderColor}`;
                                if (borderStyle === 'ornate') return `inset 0 0 0 1px ${borderColor}, inset 0 0 0 3px ${secondaryColor}, inset 0 0 0 4px ${borderColor}, inset 0 0 0 8px ${secondaryColor}, inset 0 0 0 10px ${borderColor}`;
                                return `inset 0 0 0 2px ${borderColor}`;
                            })(),
                            border: (() => {
                                const borderStyle = previewTemplate.design?.borderStyle || 'single';
                                const borderColor = previewTemplate.design?.borderColor || '#000000';
                                if (borderStyle === 'dashed') return `3px dashed ${borderColor}`;
                                return 'none';
                            })()
                        }}
                    >
                        {/* Full Frame Decorations */}
                        {previewTemplate.design?.frameDecoration?.enabled && (() => {
                            const frameStyle = previewTemplate.design.frameDecoration.style || 'vintage_border';
                            const frameColor = previewTemplate.design.frameDecoration.color || '#8B4513';
                            const frameThickness = previewTemplate.design.frameDecoration.thickness || 2;

                            const framePatterns = {
                                // Luxury certificate with ornate corners and decorative borders
                                vintage_border: (
                                    <>
                                        {/* Triple border frame */}
                                        <rect x="1" y="1" width="98" height="98" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.5} />
                                        <rect x="3.5" y="3.5" width="93" height="93" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                        <rect x="6" y="6" width="88" height="88" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />

                                        {/* Ornate corner flourishes */}
                                        <path d="M7,7 Q7,12 10,12 Q12,12 12,10 Q12,7 7,7 M7,7 L12,12 M10,7 Q10,10 7,10" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />
                                        <path d="M93,7 Q93,12 90,12 Q88,12 88,10 Q88,7 93,7 M93,7 L88,12 M90,7 Q90,10 93,10" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />
                                        <path d="M7,93 Q7,88 10,88 Q12,88 12,90 Q12,93 7,93 M7,93 L12,88 M10,93 Q10,90 7,90" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />
                                        <path d="M93,93 Q93,88 90,88 Q88,88 88,90 Q88,93 93,93 M93,93 L88,88 M90,93 Q90,90 93,90" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />

                                        {/* Decorative swirls in corners */}
                                        <path d="M10,10 Q8,13 10,16 Q13,18 16,16 Q18,13 16,10 Q13,8 10,10" fill={frameColor} opacity="0.2" />
                                        <path d="M90,10 Q92,13 90,16 Q87,18 84,16 Q82,13 84,10 Q87,8 90,10" fill={frameColor} opacity="0.2" />
                                        <path d="M10,90 Q8,87 10,84 Q13,82 16,84 Q18,87 16,90 Q13,92 10,90" fill={frameColor} opacity="0.2" />
                                        <path d="M90,90 Q92,87 90,84 Q87,82 84,84 Q82,87 84,90 Q87,92 90,90" fill={frameColor} opacity="0.2" />

                                        {/* Side ornaments */}
                                        <circle cx="50" cy="4" r="1.5" fill={frameColor} />
                                        <circle cx="50" cy="96" r="1.5" fill={frameColor} />
                                        <circle cx="4" cy="50" r="1.5" fill={frameColor} />
                                        <circle cx="96" cy="50" r="1.5" fill={frameColor} />
                                    </>
                                ),

                                // Elegant scalloped frame with wave patterns
                                elegant_frame: (
                                    <>
                                        {/* Main decorative border */}
                                        <rect x="2" y="2" width="96" height="96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.2} />

                                        {/* Scalloped inner border - top */}
                                        <path d="M8,5 Q10,3 12,5 Q14,3 16,5 Q18,3 20,5 Q22,3 24,5 Q26,3 28,5 Q30,3 32,5 Q34,3 36,5 Q38,3 40,5 Q42,3 44,5 Q46,3 48,5 Q50,3 52,5 Q54,3 56,5 Q58,3 60,5 Q62,3 64,5 Q66,3 68,5 Q70,3 72,5 Q74,3 76,5 Q78,3 80,5 Q82,3 84,5 Q86,3 88,5 Q90,3 92,5"
                                            stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />

                                        {/* Bottom scallops */}
                                        <path d="M8,95 Q10,97 12,95 Q14,97 16,95 Q18,97 20,95 Q22,97 24,95 Q26,97 28,95 Q30,97 32,95 Q34,97 36,95 Q38,97 40,95 Q42,97 44,95 Q46,97 48,95 Q50,97 52,95 Q54,97 56,95 Q58,97 60,95 Q62,97 64,95 Q66,97 68,95 Q70,97 72,95 Q74,97 76,95 Q78,97 80,95 Q82,97 84,95 Q86,97 88,95 Q90,97 92,95"
                                            stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />

                                        {/* Left scallops */}
                                        <path d="M5,8 Q3,10 5,12 Q3,14 5,16 Q3,18 5,20 Q3,22 5,24 Q3,26 5,28 Q3,30 5,32 Q3,34 5,36 Q3,38 5,40 Q3,42 5,44 Q3,46 5,48 Q3,50 5,52 Q3,54 5,56 Q3,58 5,60 Q3,62 5,64 Q3,66 5,68 Q3,70 5,72 Q3,74 5,76 Q3,78 5,80 Q3,82 5,84 Q3,86 5,88 Q3,90 5,92"
                                            stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />

                                        {/* Right scallops */}
                                        <path d="M95,8 Q97,10 95,12 Q97,14 95,16 Q97,18 95,20 Q97,22 95,24 Q97,26 95,28 Q97,30 95,32 Q97,34 95,36 Q97,38 95,40 Q97,42 95,44 Q97,46 95,48 Q97,50 95,52 Q97,54 95,56 Q97,58 95,60 Q97,62 95,64 Q97,66 95,68 Q97,70 95,72 Q97,74 95,76 Q97,78 95,80 Q97,82 95,84 Q97,86 95,88 Q97,90 95,92"
                                            stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />

                                        {/* Corner embellishments */}
                                        <circle cx="8" cy="8" r="2" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="92" cy="8" r="2" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="8" cy="92" r="2" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="92" cy="92" r="2" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    </>
                                ),

                                // Ornate multi-layer frame with filigree
                                ornate_full: (
                                    <>
                                        {/* Outer ornate border */}
                                        <rect x="1" y="1" width="98" height="98" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.5} />
                                        <rect x="3" y="3" width="94" height="94" fill="none" stroke={frameColor} strokeWidth={frameThickness} />
                                        <rect x="5.5" y="5.5" width="89" height="89" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <rect x="8" y="8" width="84" height="84" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />

                                        {/* Ornamental corner motifs */}
                                        <path d="M5,5 Q5,15 12,15 Q15,15 15,12 Q15,5 5,5 M5,8 L15,12 M8,5 L12,15" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                        <path d="M95,5 Q95,15 88,15 Q85,15 85,12 Q85,5 95,5 M95,8 L85,12 M92,5 L88,15" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                        <path d="M5,95 Q5,85 12,85 Q15,85 15,88 Q15,95 5,95 M5,92 L15,88 M8,95 L12,85" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                        <path d="M95,95 Q95,85 88,85 Q85,85 85,88 Q85,95 95,95 M95,92 L85,88 M92,95 L88,85" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />

                                        {/* Decorative filigree fills */}
                                        <circle cx="50" cy="5" r="2" fill={frameColor} />
                                        <circle cx="50" cy="95" r="2" fill={frameColor} />
                                        <circle cx="5" cy="50" r="2" fill={frameColor} />
                                        <circle cx="95" cy="50" r="2" fill={frameColor} />
                                    </>
                                ),

                                // Art deco geometric frame
                                art_deco_frame: (
                                    <>
                                        {/* Primary geometric borders */}
                                        <rect x="2" y="2" width="96" height="96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.8} />
                                        <rect x="8" y="8" width="84" height="84" fill="none" stroke={frameColor} strokeWidth={frameThickness} />

                                        {/* Deco corner elements */}
                                        <path d="M2,2 L18,18 M2,18 L18,2" stroke={frameColor} strokeWidth={frameThickness * 1.2} fill="none" />
                                        <path d="M98,2 L82,18 M98,18 L82,2" stroke={frameColor} strokeWidth={frameThickness * 1.2} fill="none" />
                                        <path d="M2,98 L18,82 M2,82 L18,98" stroke={frameColor} strokeWidth={frameThickness * 1.2} fill="none" />
                                        <path d="M98,98 L82,82 M98,82 L82,98" stroke={frameColor} strokeWidth={frameThickness * 1.2} fill="none" />

                                        {/* Corner brackets */}
                                        <path d="M2,25 L2,2 L25,2" stroke={frameColor} strokeWidth={frameThickness * 2.5} fill="none" />
                                        <path d="M98,25 L98,2 L75,2" stroke={frameColor} strokeWidth={frameThickness * 2.5} fill="none" />
                                        <path d="M2,75 L2,98 L25,98" stroke={frameColor} strokeWidth={frameThickness * 2.5} fill="none" />
                                        <path d="M98,75 L98,98 L75,98" stroke={frameColor} strokeWidth={frameThickness * 2.5} fill="none" />

                                        {/* Step patterns */}
                                        <path d="M35,2 L35,8 L40,8 L40,2 M60,2 L60,8 L65,8 L65,2" stroke={frameColor} strokeWidth={frameThickness * 0.8} fill="none" />
                                        <path d="M35,98 L35,92 L40,92 L40,98 M60,98 L60,92 L65,92 L65,98" stroke={frameColor} strokeWidth={frameThickness * 0.8} fill="none" />
                                        <path d="M2,35 L8,35 L8,40 L2,40 M2,60 L8,60 L8,65 L2,65" stroke={frameColor} strokeWidth={frameThickness * 0.8} fill="none" />
                                        <path d="M98,35 L92,35 L92,40 L98,40 M98,60 L92,60 L92,65 L98,65" stroke={frameColor} strokeWidth={frameThickness * 0.8} fill="none" />
                                    </>
                                ),

                                // Baroque ornamental frame
                                baroque_frame: (
                                    <>
                                        {/* Main baroque border */}
                                        <rect x="2" y="2" width="96" height="96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 2} />

                                        {/* Scrollwork - top */}
                                        <path d="M8,6 Q10,4 12,6 T16,6 T20,6 T24,6 T28,6 T32,6 T36,6 T40,6 T44,6 T48,6 T52,6 T56,6 T60,6 T64,6 T68,6 T72,6 T76,6 T80,6 T84,6 T88,6 T92,6"
                                            stroke={frameColor} strokeWidth={frameThickness * 0.8} fill="none" />

                                        {/* Scrollwork - bottom */}
                                        <path d="M8,94 Q10,96 12,94 T16,94 T20,94 T24,94 T28,94 T32,94 T36,94 T40,94 T44,94 T48,94 T52,94 T56,94 T60,94 T64,94 T68,94 T72,94 T76,94 T80,94 T84,94 T88,94 T92,94"
                                            stroke={frameColor} strokeWidth={frameThickness * 0.8} fill="none" />

                                        {/* Scrollwork - left */}
                                        <path d="M6,8 Q4,10 6,12 T6,16 T6,20 T6,24 T6,28 T6,32 T6,36 T6,40 T6,44 T6,48 T6,52 T6,56 T6,60 T6,64 T6,68 T6,72 T6,76 T6,80 T6,84 T6,88 T6,92"
                                            stroke={frameColor} strokeWidth={frameThickness * 0.8} fill="none" />

                                        {/* Scrollwork - right */}
                                        <path d="M94,8 Q96,10 94,12 T94,16 T94,20 T94,24 T94,28 T94,32 T94,36 T94,40 T94,44 T94,48 T94,52 T94,56 T94,60 T94,64 T94,68 T94,72 T94,76 T94,80 T94,84 T94,88 T94,92"
                                            stroke={frameColor} strokeWidth={frameThickness * 0.8} fill="none" />

                                        {/* Corner rosettes */}
                                        <circle cx="10" cy="10" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <circle cx="90" cy="10" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <circle cx="10" cy="90" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                        <circle cx="90" cy="90" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />

                                        {/* Center ornaments */}
                                        <circle cx="50" cy="6" r="2" fill={frameColor} />
                                        <circle cx="50" cy="94" r="2" fill={frameColor} />
                                        <circle cx="6" cy="50" r="2" fill={frameColor} />
                                        <circle cx="94" cy="50" r="2" fill={frameColor} />
                                    </>
                                ),

                                // Modern minimalist frame
                                modern_frame: (
                                    <>
                                        {/* Simple clean borders */}
                                        <rect x="4" y="4" width="92" height="92" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.5} />

                                        {/* Corner accents - top left */}
                                        <line x1="1" y1="1" x2="18" y2="1" stroke={frameColor} strokeWidth={frameThickness * 3.5} />
                                        <line x1="1" y1="1" x2="1" y2="18" stroke={frameColor} strokeWidth={frameThickness * 3.5} />

                                        {/* Corner accents - top right */}
                                        <line x1="99" y1="1" x2="82" y2="1" stroke={frameColor} strokeWidth={frameThickness * 3.5} />
                                        <line x1="99" y1="1" x2="99" y2="18" stroke={frameColor} strokeWidth={frameThickness * 3.5} />

                                        {/* Corner accents - bottom left */}
                                        <line x1="1" y1="99" x2="18" y2="99" stroke={frameColor} strokeWidth={frameThickness * 3.5} />
                                        <line x1="1" y1="99" x2="1" y2="82" stroke={frameColor} strokeWidth={frameThickness * 3.5} />

                                        {/* Corner accents - bottom right */}
                                        <line x1="99" y1="99" x2="82" y2="99" stroke={frameColor} strokeWidth={frameThickness * 3.5} />
                                        <line x1="99" y1="99" x2="99" y2="82" stroke={frameColor} strokeWidth={frameThickness * 3.5} />

                                        {/* Mid-point markers */}
                                        <rect x="48" y="1" width="4" height="7" fill={frameColor} />
                                        <rect x="48" y="92" width="4" height="7" fill={frameColor} />
                                        <rect x="1" y="48" width="7" height="4" fill={frameColor} />
                                        <rect x="92" y="48" width="7" height="4" fill={frameColor} />
                                    </>
                                ),

                                // Islamic geometric frame
                                islamic_frame: (
                                    <>
                                        {/* Primary islamic border */}
                                        <rect x="2" y="2" width="96" height="96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.8} />
                                        <rect x="5" y="5" width="90" height="90" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />

                                        {/* Islamic geometric corner motifs */}
                                        <path d="M8,8 L16,12 L12,16 Z" fill={frameColor} opacity="0.7" />
                                        <path d="M92,8 L84,12 L88,16 Z" fill={frameColor} opacity="0.7" />
                                        <path d="M8,92 L16,88 L12,84 Z" fill={frameColor} opacity="0.7" />
                                        <path d="M92,92 L84,88 L88,84 Z" fill={frameColor} opacity="0.7" />

                                        {/* Star elements */}
                                        <path d="M50,5 L52,9 L48,9 Z" fill={frameColor} />
                                        <path d="M50,95 L52,91 L48,91 Z" fill={frameColor} />
                                        <path d="M5,50 L9,52 L9,48 Z" fill={frameColor} />
                                        <path d="M95,50 L91,52 L91,48 Z" fill={frameColor} />

                                        {/* Decorative dots */}
                                        <circle cx="25" cy="8" r="1.5" fill={frameColor} />
                                        <circle cx="75" cy="8" r="1.5" fill={frameColor} />
                                        <circle cx="25" cy="92" r="1.5" fill={frameColor} />
                                        <circle cx="75" cy="92" r="1.5" fill={frameColor} />
                                        <circle cx="8" cy="25" r="1.5" fill={frameColor} />
                                        <circle cx="8" cy="75" r="1.5" fill={frameColor} />
                                        <circle cx="92" cy="25" r="1.5" fill={frameColor} />
                                        <circle cx="92" cy="75" r="1.5" fill={frameColor} />
                                    </>
                                ),

                                // Modern Islamic frame
                                modern_islamic: (
                                    <>
                                        {/* Clean islamic borders */}
                                        <rect x="1" y="1" width="98" height="98" fill="none" stroke={frameColor} strokeWidth={frameThickness * 2.2} />
                                        <rect x="4" y="4" width="92" height="92" fill="none" stroke={frameColor} strokeWidth={frameThickness} />

                                        {/* Modern geometric corners */}
                                        <path d="M8,8 L14,10 L11,13 L8,8" fill={frameColor} />
                                        <path d="M92,8 L86,10 L89,13 L92,8" fill={frameColor} />
                                        <path d="M8,92 L14,90 L11,87 L8,92" fill={frameColor} />
                                        <path d="M92,92 L86,90 L89,87 L92,92" fill={frameColor} />

                                        {/* Modern star accents */}
                                        <path d="M50,3 L52,7 L48,7 Z" fill={frameColor} />
                                        <path d="M50,97 L52,93 L48,93 Z" fill={frameColor} />
                                        <path d="M3,50 L7,52 L7,48 Z" fill={frameColor} />
                                        <path d="M97,50 L93,52 L93,48 Z" fill={frameColor} />

                                        {/* Contemporary pattern dots */}
                                        <circle cx="28" cy="8" r="1.8" fill={frameColor} />
                                        <circle cx="72" cy="8" r="1.8" fill={frameColor} />
                                        <circle cx="8" cy="28" r="1.8" fill={frameColor} />
                                        <circle cx="8" cy="72" r="1.8" fill={frameColor} />
                                        <circle cx="92" cy="28" r="1.8" fill={frameColor} />
                                        <circle cx="92" cy="72" r="1.8" fill={frameColor} />
                                        <circle cx="28" cy="92" r="1.8" fill={frameColor} />
                                        <circle cx="72" cy="92" r="1.8" fill={frameColor} />
                                    </>
                                ),

                                // Classic Islamic frame
                                classic_islamic: (
                                    <>
                                        {/* Traditional triple border */}
                                        <rect x="1" y="1" width="98" height="98" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.8} />
                                        <rect x="4" y="4" width="92" height="92" fill="none" stroke={frameColor} strokeWidth={frameThickness} />
                                        <rect x="7" y="7" width="86" height="86" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />

                                        {/* Classic islamic diamond corners */}
                                        <path d="M8,8 L12,12 L8,16 L4,12 Z" fill={frameColor} opacity="0.75" />
                                        <path d="M92,8 L96,12 L92,16 L88,12 Z" fill={frameColor} opacity="0.75" />
                                        <path d="M8,92 L12,88 L8,84 L4,88 Z" fill={frameColor} opacity="0.75" />
                                        <path d="M92,92 L96,88 L92,84 L88,88 Z" fill={frameColor} opacity="0.75" />

                                        {/* Traditional stars */}
                                        <path d="M50,4 L52,8 L48,8 Z" fill={frameColor} />
                                        <path d="M50,96 L52,92 L48,92 Z" fill={frameColor} />
                                        <path d="M4,50 L8,52 L8,48 Z" fill={frameColor} />
                                        <path d="M96,50 L92,52 L92,48 Z" fill={frameColor} />

                                        {/* Pattern embellishments */}
                                        <circle cx="22" cy="8" r="1.4" fill={frameColor} />
                                        <circle cx="78" cy="8" r="1.4" fill={frameColor} />
                                        <circle cx="22" cy="92" r="1.4" fill={frameColor} />
                                        <circle cx="78" cy="92" r="1.4" fill={frameColor} />
                                        <circle cx="8" cy="22" r="1.4" fill={frameColor} />
                                        <circle cx="8" cy="78" r="1.4" fill={frameColor} />
                                        <circle cx="92" cy="22" r="1.4" fill={frameColor} />
                                        <circle cx="92" cy="78" r="1.4" fill={frameColor} />
                                    </>
                                ),

                                // Modern Ornate - elaborate scrollwork frame like the certificate image
                                modern_ornate: (
                                    <>
                                        {/* Triple border system */}
                                        <rect x="1.5" y="1.5" width="97" height="97" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.8} />
                                        <rect x="3.5" y="3.5" width="93" height="93" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.2} />
                                        <rect x="6" y="6" width="88" height="88" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />

                                        {/* Continuous scrollwork on top border */}
                                        <path d="M8,4 Q10,2 12,4 Q14,6 16,4 Q18,2 20,4 Q22,6 24,4 Q26,2 28,4 Q30,6 32,4 Q34,2 36,4 Q38,6 40,4 Q42,2 44,4 Q46,6 48,4 Q50,2 52,4 Q54,6 56,4 Q58,2 60,4 Q62,6 64,4 Q66,2 68,4 Q70,6 72,4 Q74,2 76,4 Q78,6 80,4 Q82,2 84,4 Q86,6 88,4 Q90,2 92,4"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M10,2.5 Q12,1 14,2.5 M18,2.5 Q20,1 22,2.5 M26,2.5 Q28,1 30,2.5 M34,2.5 Q36,1 38,2.5 M42,2.5 Q44,1 46,2.5 M50,2.5 Q52,1 54,2.5 M58,2.5 Q60,1 62,2.5 M66,2.5 Q68,1 70,2.5 M74,2.5 Q76,1 78,2.5 M82,2.5 Q84,1 86,2.5 M90,2.5 Q92,1 94,2.5"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />

                                        {/* Bottom scrollwork */}
                                        <path d="M8,96 Q10,98 12,96 Q14,94 16,96 Q18,98 20,96 Q22,94 24,96 Q26,98 28,96 Q30,94 32,96 Q34,98 36,96 Q38,94 40,96 Q42,98 44,96 Q46,94 48,96 Q50,98 52,96 Q54,94 56,96 Q58,98 60,96 Q62,94 64,96 Q66,98 68,96 Q70,94 72,96 Q74,98 76,96 Q78,94 80,96 Q82,98 84,96 Q86,94 88,96 Q90,98 92,96"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M10,97.5 Q12,99 14,97.5 M18,97.5 Q20,99 22,97.5 M26,97.5 Q28,99 30,97.5 M34,97.5 Q36,99 38,97.5 M42,97.5 Q44,99 46,97.5 M50,97.5 Q52,99 54,97.5 M58,97.5 Q60,99 62,97.5 M66,97.5 Q68,99 70,97.5 M74,97.5 Q76,99 78,97.5 M82,97.5 Q84,99 86,97.5 M90,97.5 Q92,99 94,97.5"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />

                                        {/* Left side scrollwork */}
                                        <path d="M4,8 Q2,10 4,12 Q6,14 4,16 Q2,18 4,20 Q6,22 4,24 Q2,26 4,28 Q6,30 4,32 Q2,34 4,36 Q6,38 4,40 Q2,42 4,44 Q6,46 4,48 Q2,50 4,52 Q6,54 4,56 Q2,58 4,60 Q6,62 4,64 Q2,66 4,68 Q6,70 4,72 Q2,74 4,76 Q6,78 4,80 Q2,82 4,84 Q6,86 4,88 Q2,90 4,92"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M2.5,10 Q1,12 2.5,14 M2.5,18 Q1,20 2.5,22 M2.5,26 Q1,28 2.5,30 M2.5,34 Q1,36 2.5,38 M2.5,42 Q1,44 2.5,46 M2.5,50 Q1,52 2.5,54 M2.5,58 Q1,60 2.5,62 M2.5,66 Q1,68 2.5,70 M2.5,74 Q1,76 2.5,78 M2.5,82 Q1,84 2.5,86 M2.5,90 Q1,92 2.5,94"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />

                                        {/* Right side scrollwork */}
                                        <path d="M96,8 Q98,10 96,12 Q94,14 96,16 Q98,18 96,20 Q94,22 96,24 Q98,26 96,28 Q94,30 96,32 Q98,34 96,36 Q94,38 96,40 Q98,42 96,44 Q94,46 96,48 Q98,50 96,52 Q94,54 96,56 Q98,58 96,60 Q94,62 96,64 Q98,66 96,68 Q94,70 96,72 Q98,74 96,76 Q94,78 96,80 Q98,82 96,84 Q94,86 96,88 Q98,90 96,92"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M97.5,10 Q99,12 97.5,14 M97.5,18 Q99,20 97.5,22 M97.5,26 Q99,28 97.5,30 M97.5,34 Q99,36 97.5,38 M97.5,42 Q99,44 97.5,46 M97.5,50 Q99,52 97.5,54 M97.5,58 Q99,60 97.5,62 M97.5,66 Q99,68 97.5,70 M97.5,74 Q99,76 97.5,78 M97.5,82 Q99,84 97.5,86 M97.5,90 Q99,92 97.5,94"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />

                                        {/* Ornate corner flourishes */}
                                        <path d="M8,8 Q6,10 8,12 Q10,14 12,12 Q14,10 12,8 Q10,6 8,8 M8,8 Q10,6 12,8 M8,8 Q6,10 8,12 M12,8 Q14,10 12,12"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                        <circle cx="10" cy="10" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="10" cy="10" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.3} />

                                        <path d="M92,8 Q94,10 92,12 Q90,14 88,12 Q86,10 88,8 Q90,6 92,8 M92,8 Q90,6 88,8 M92,8 Q94,10 92,12 M88,8 Q86,10 88,12"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                        <circle cx="90" cy="10" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="90" cy="10" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.3} />

                                        <path d="M8,92 Q6,90 8,88 Q10,86 12,88 Q14,90 12,92 Q10,94 8,92 M8,92 Q10,94 12,92 M8,92 Q6,90 8,88 M12,92 Q14,90 12,88"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                        <circle cx="10" cy="90" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="10" cy="90" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.3} />

                                        <path d="M92,92 Q94,90 92,88 Q90,86 88,88 Q86,90 88,92 Q90,94 92,92 M92,92 Q90,94 88,92 M92,92 Q94,90 92,88 M88,92 Q86,90 88,88"
                                            fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                        <circle cx="90" cy="90" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <circle cx="90" cy="90" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.3} />

                                        {/* Decorative swirls in corner areas */}
                                        <path d="M15,8 Q18,6 20,8 Q22,10 20,13 Q18,15 15,13 Q13,10 15,8" fill={frameColor} opacity="0.2" />
                                        <path d="M85,8 Q82,6 80,8 Q78,10 80,13 Q82,15 85,13 Q87,10 85,8" fill={frameColor} opacity="0.2" />
                                        <path d="M15,92 Q18,94 20,92 Q22,90 20,87 Q18,85 15,87 Q13,90 15,92" fill={frameColor} opacity="0.2" />
                                        <path d="M85,92 Q82,94 80,92 Q78,90 80,87 Q82,85 85,87 Q87,90 85,92" fill={frameColor} opacity="0.2" />

                                        {/* Center ornamental elements */}
                                        <circle cx="50" cy="3" r="2" fill={frameColor} />
                                        <circle cx="50" cy="97" r="2" fill={frameColor} />
                                        <circle cx="3" cy="50" r="2" fill={frameColor} />
                                        <circle cx="97" cy="50" r="2" fill={frameColor} />

                                        {/* Additional decorative flourishes */}
                                        <path d="M25,5 Q27,3 29,5 M71,5 Q73,3 75,5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M25,95 Q27,97 29,95 M71,95 Q73,97 75,95" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M5,25 Q3,27 5,29 M5,71 Q3,73 5,75" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                        <path d="M95,25 Q97,27 95,29 M95,71 Q97,73 95,75" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    </>
                                )
                            };

                            return (
                                <svg
                                    viewBox="0 0 100 100"
                                    preserveAspectRatio="none"
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    {framePatterns[frameStyle] || framePatterns.vintage_border}
                                </svg>
                            );
                        })()}

                        {/* Border Pattern Overlay */}
                        {(() => {
                            const borderPattern = previewTemplate.design?.borderPattern || 'none';
                            const borderColor = previewTemplate.design?.borderColor || '#000000';

                            if (borderPattern === 'none') return null;

                            const patterns = {
                                dots: 'M0,5 L0,5 M10,5 L10,5 M20,5 L20,5 M30,5 L30,5 M40,5 L40,5 M50,5 L50,5 M60,5 L60,5 M70,5 L70,5 M80,5 L80,5 M90,5 L90,5 M100,5 L100,5',
                                waves: 'M0,5 Q5,0 10,5 T20,5 T30,5 T40,5 T50,5 T60,5 T70,5 T80,5 T90,5 T100,5',
                                zigzag: 'M0,8 L5,2 L10,8 L15,2 L20,8 L25,2 L30,8 L35,2 L40,8 L45,2 L50,8 L55,2 L60,8 L65,2 L70,8 L75,2 L80,8 L85,2 L90,8 L95,2 L100,8',
                                chain: 'M0,5 Q5,0 10,5 Q15,10 20,5 Q25,0 30,5 Q35,10 40,5 Q45,0 50,5 Q55,10 60,5 Q65,0 70,5 Q75,10 80,5 Q85,0 90,5 Q95,10 100,5'
                            };

                            return (
                                <>
                                    <svg viewBox="0 0 100 10" preserveAspectRatio="none" style={{ position: 'absolute', top: '5px', left: '5px', width: 'calc(100% - 10px)', height: '15px' }}>
                                        <path d={patterns[borderPattern]} fill="none" stroke={borderColor} strokeWidth="1.5" />
                                    </svg>
                                    <svg viewBox="0 0 100 10" preserveAspectRatio="none" style={{ position: 'absolute', bottom: '5px', left: '5px', width: 'calc(100% - 10px)', height: '15px' }}>
                                        <path d={patterns[borderPattern]} fill="none" stroke={borderColor} strokeWidth="1.5" />
                                    </svg>
                                    <svg viewBox="0 0 10 100" preserveAspectRatio="none" style={{ position: 'absolute', top: '5px', left: '5px', width: '15px', height: 'calc(100% - 10px)' }}>
                                        <path d={patterns[borderPattern].replace(/M0,5/g, 'M5,0').replace(/L(\d+),5/g, 'L5,$1').replace(/Q(\d+),(\d+)/g, 'Q$2,$1').replace(/T(\d+),5/g, 'T5,$1')} fill="none" stroke={borderColor} strokeWidth="1.5" />
                                    </svg>
                                    <svg viewBox="0 0 10 100" preserveAspectRatio="none" style={{ position: 'absolute', top: '5px', right: '5px', width: '15px', height: 'calc(100% - 10px)' }}>
                                        <path d={patterns[borderPattern].replace(/M0,5/g, 'M5,0').replace(/L(\d+),5/g, 'L5,$1').replace(/Q(\d+),(\d+)/g, 'Q$2,$1').replace(/T(\d+),5/g, 'T5,$1')} fill="none" stroke={borderColor} strokeWidth="1.5" />
                                    </svg>
                                </>
                            );
                        })()}
                        {/* Corner Decorations */}
                        {previewTemplate.design?.cornerDecoration?.enabled && (() => {
                            const decorationType = previewTemplate.design.cornerDecoration.type || 'floral';
                            const decorationSize = previewTemplate.design.cornerDecoration.size || 50;
                            const decorationColor = previewTemplate.design.cornerDecoration.color || '#DAA520';

                            const decorations = {
                                floral: 'M0,0 L0,65 Q5,58 10,53 L22,41 Q28,35 35,35 Q40,35 44,39 L50,45 M0,18 Q10,16 17,21 Q22,25 25,32 Q27,38 32,42 L40,48 M12,0 Q12,14 20,22 Q26,28 34,31 Q41,34 47,38 L55,45 M0,36 Q14,34 22,40 Q28,45 30,53 L34,62 M30,0 Q27,18 36,28 Q42,35 50,38 L58,42 M5,5 Q8,8 8,12 Q8,16 5,19 Q2,22 -2,22 M15,15 Q19,18 19,23 Q19,28 15,31 M25,25 Q30,28 30,34 Q30,40 25,43 M35,35 Q41,38 41,45 M45,5 L50,10 L47,13 L42,8 M55,15 L60,20 L58,22 L53,17',
                                geometric: 'M0,0 L0,80 L5,75 L5,5 L75,5 L80,0 M10,10 L10,70 L15,65 L15,15 L65,15 L70,10 M20,20 L20,60 L25,55 L25,25 L55,25 L60,20 M30,30 L30,50 L35,45 L35,35 L45,35 L50,30 M0,15 L15,0 M0,30 L30,0 M0,45 L45,0 M0,60 L60,0 M0,75 L75,0',
                                ornamental: 'M0,0 L0,70 Q5,60 10,55 L25,40 Q30,35 35,35 Q40,35 45,40 L55,50 M0,20 Q8,18 15,22 Q20,25 23,30 Q25,35 30,38 L40,45 Q45,48 50,48 M10,0 Q10,15 18,23 Q23,28 30,30 Q38,32 43,37 L53,47 M25,0 Q22,20 32,30 Q38,36 47,38 Q55,40 60,45 L70,55 M0,40 Q12,38 20,43 Q26,47 28,55 L32,65 M40,0 Q35,25 45,38 Q52,47 62,50 L72,53',
                                islamic: 'M0,0 L0,80 Q5,70 12,63 L28,47 Q35,40 42,40 L50,40 M0,20 Q10,18 18,24 Q24,29 28,36 Q32,42 38,46 L48,54 M10,0 Q10,18 20,28 Q26,34 35,37 Q43,40 50,45 L60,55 M0,40 Q15,38 25,45 Q32,50 35,58 L40,68 M30,0 Q27,22 38,35 Q45,43 55,47 L65,52 M0,60 Q20,58 30,65 L38,73 M50,0 Q45,30 58,45 L68,55 M20,0 L30,10 L25,15 L15,5 M40,0 L50,10 L48,12 L38,2 M60,0 L70,10 L68,12 L58,2',
                                arabesque: 'M0,0 L0,60 Q0,50 8,42 Q15,35 25,32 Q35,29 42,35 L50,43 M0,15 Q10,15 18,22 Q25,28 28,38 Q30,45 35,50 L45,60 M0,30 Q15,28 25,35 Q32,40 35,50 Q37,58 42,65 L50,73 M15,0 Q15,12 22,20 Q28,27 38,30 Q48,32 55,38 L65,48 M30,0 Q28,15 35,25 Q40,32 50,35 Q58,37 65,42 L73,50 M45,0 Q42,18 50,28 Q55,35 65,40 L75,48',
                                baroque: 'M0,0 L0,75 Q3,68 8,63 Q15,56 23,52 Q30,49 38,50 Q45,51 50,56 L58,64 M0,18 Q8,16 15,20 Q21,24 25,30 Q28,36 33,40 Q38,44 44,46 L52,49 M12,0 Q12,12 18,20 Q23,27 30,31 Q37,35 44,37 Q51,39 57,43 L65,50 M0,35 Q10,33 18,38 Q24,42 27,49 Q29,55 33,60 L40,68 M28,0 Q26,15 33,25 Q38,33 46,38 Q53,42 60,45 L68,49 M0,52 Q14,50 22,56 Q28,61 30,68 L33,76 M45,0 Q42,20 50,32 Q56,41 65,46 L73,50 M15,0 L22,7 Q25,10 25,15 L25,22 M35,0 L42,7 Q48,13 52,20 L58,30 M55,0 L62,7 Q68,13 72,22 L76,32',
                                celtic: 'M0,0 L0,80 Q8,72 16,68 L32,60 Q40,56 48,60 L56,64 M0,24 Q12,20 20,26 Q26,31 28,40 Q30,48 36,54 L44,60 M16,0 Q16,16 24,28 Q30,36 40,40 Q48,44 56,50 L64,56 M0,48 Q16,44 26,52 Q32,58 34,68 L38,78 M40,0 Q36,24 46,38 Q52,48 62,54 L72,60 M8,8 L16,16 L12,20 L4,12 M24,24 L32,32 L28,36 L20,28 M40,40 L48,48 L44,52 L36,44 M56,8 L64,16 L60,20 L52,12 M12,0 Q10,10 16,16 M28,0 Q24,12 32,20 M44,0 Q38,14 48,24 M60,0 Q52,16 64,28',
                                art_deco: 'M0,0 L0,80 L3,80 L3,3 L80,3 L80,0 M8,8 L8,72 L10,72 L10,10 L72,10 L72,8 M15,15 L15,65 L17,65 L17,17 L65,17 L65,15 M22,22 L22,58 L24,58 L24,24 L58,24 L58,22 M29,29 L29,51 L31,51 L31,31 L51,31 L51,29 M36,36 L36,44 L38,44 L38,38 L44,38 L44,36 M0,12 L12,0 M0,25 L25,0 M0,38 L38,0 M0,51 L51,0 M0,64 L64,0 M0,77 L77,0',
                                vintage: 'M0,0 L0,72 Q6,64 14,58 L28,44 Q36,36 44,38 L52,40 M0,20 Q10,18 18,24 Q24,29 27,36 Q30,43 36,48 L44,54 M14,0 Q14,18 24,28 Q30,34 39,37 Q47,40 54,45 L62,52 M0,40 Q16,38 24,46 Q30,52 32,60 L36,70 M34,0 Q30,26 42,38 Q48,46 58,50 L68,54 M8,8 Q12,12 12,18 Q12,24 8,28 Q4,32 0,32 M18,18 Q24,22 24,30 Q24,38 18,42 M28,28 Q35,32 35,40 M38,8 L44,14 L41,17 L35,11 M48,18 L54,24 L52,26 L46,20 M58,28 L64,34 L62,36 L56,30',
                                modern_curves: 'M0,0 L0,80 M0,15 L65,15 M0,30 L50,30 M0,45 L35,45 M0,60 L20,60 M15,0 L15,65 M30,0 L30,50 M45,0 L45,35 M60,0 L60,20 M0,0 L80,80 M10,0 L70,60 M20,0 L60,40 M30,0 L50,20 M40,0 L40,0',
                                elegant_swirl: 'M0,0 L0,60 Q0,50 8,42 Q15,35 25,32 Q35,29 42,35 L50,43 M0,15 Q10,15 18,22 Q25,28 28,38 Q30,45 35,50 L45,60 M0,30 Q15,28 25,35 Q32,40 35,50 Q37,58 42,65 L50,73 M15,0 Q15,12 22,20 Q28,27 38,30 Q48,32 55,38 L65,48 M30,0 Q28,15 35,25 Q40,32 50,35 Q58,37 65,42 L73,50 M45,0 Q42,18 50,28 Q55,35 65,40 L75,48',
                                royal: 'M0,0 L0,70 Q5,62 12,55 L30,37 Q38,29 47,32 L55,35 M0,22 Q12,20 22,27 Q28,32 32,40 Q35,47 42,52 L52,60 M15,0 Q15,20 27,32 Q34,39 44,42 Q53,45 60,50 L70,58 M0,45 Q18,43 28,52 Q34,58 37,67 L42,77 M38,0 Q34,28 47,42 Q55,50 66,54 L76,58 M18,0 L28,10 Q32,14 32,20 L32,30 M40,0 L50,10 Q56,16 60,24 L66,36 M10,0 L15,5 L12,8 L7,3 M25,0 L32,7 L29,10 L22,3 M55,0 L62,7 L60,9 L53,2'
                            };

                            const path = decorations[decorationType] || decorations.floral;

                            return (
                                <>
                                    {/* Top Left */}
                                    <svg viewBox="0 0 100 100" style={{ position: 'absolute', top: '10px', left: '10px', width: `${decorationSize}px`, height: `${decorationSize}px` }}>
                                        <path d={path} fill={decorationColor} />
                                    </svg>
                                    {/* Top Right */}
                                    <svg viewBox="0 0 100 100" style={{ position: 'absolute', top: '10px', right: '10px', width: `${decorationSize}px`, height: `${decorationSize}px`, transform: 'scaleX(-1)' }}>
                                        <path d={path} fill={decorationColor} />
                                    </svg>
                                    {/* Bottom Left */}
                                    <svg viewBox="0 0 100 100" style={{ position: 'absolute', bottom: '10px', left: '10px', width: `${decorationSize}px`, height: `${decorationSize}px`, transform: 'scaleY(-1)' }}>
                                        <path d={path} fill={decorationColor} />
                                    </svg>
                                    {/* Bottom Right */}
                                    <svg viewBox="0 0 100 100" style={{ position: 'absolute', bottom: '10px', right: '10px', width: `${decorationSize}px`, height: `${decorationSize}px`, transform: 'scale(-1)' }}>
                                        <path d={path} fill={decorationColor} />
                                    </svg>
                                </>
                            );
                        })()}

                        {/* Header */}
                        {previewTemplate.header?.logo && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: (() => {
                                        const align = previewTemplate.design?.layoutSettings?.logoAlign || 'center';
                                        if (align === 'left') return 'flex-start';
                                        if (align === 'right') return 'flex-end';
                                        return 'center';
                                    })(),
                                    marginBottom: `${previewTemplate.design?.layoutSettings?.headerSpacing || 30}px`,
                                    marginTop: `${previewTemplate.design?.layoutSettings?.logoOffsetTop || 0}px`,
                                    position: 'relative',
                                    transform: `translate(${previewTemplate.design?.layoutSettings?.logoOffsetX || 0}px, ${previewTemplate.design?.layoutSettings?.logoOffsetY || 0}px)`
                                }}
                            >
                                <img
                                    src={`${config.baseUrl}${previewTemplate.header.logo}`}
                                    alt="Logo"
                                    style={{
                                        maxWidth: `${previewTemplate.design?.layoutSettings?.logoMaxWidth || 150}px`,
                                        maxHeight: `${previewTemplate.design?.layoutSettings?.logoMaxHeight || 100}px`,
                                        objectFit: 'contain'
                                    }}
                                    onError={(e) => {
                                        console.error('Logo failed to load:', e.target.src);
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        )}
                        <h1
                            style={{
                                fontFamily: previewTemplate.header?.instituteNameFont?.family || 'Arial',
                                fontSize: `${previewTemplate.header?.instituteNameFont?.size || 24}px`,
                                color: previewTemplate.header?.instituteNameFont?.color || '#000000',
                                textAlign: previewTemplate.design?.layoutSettings?.headerAlign || 'center',
                                marginBottom: `${previewTemplate.design?.layoutSettings?.headerSpacing || 30}px`,
                                position: 'relative',
                                transform: `translate(${previewTemplate.design?.layoutSettings?.headerOffsetX || 0}px, ${previewTemplate.design?.layoutSettings?.headerOffsetY || 0}px)`
                            }}
                        >
                            {previewTemplate.header?.instituteName || 'Institute Name'}
                        </h1>

                        {/* Body */}
                        <h2
                            style={{
                                fontFamily: previewTemplate.body?.title?.font?.family || 'Arial',
                                fontSize: `${previewTemplate.body?.title?.font?.size || 36}px`,
                                color: previewTemplate.body?.title?.font?.color || '#000000',
                                textAlign: previewTemplate.design?.layoutSettings?.titleAlign || 'center',
                                marginBottom: `${previewTemplate.design?.layoutSettings?.titleSpacing || 20}px`,
                                position: 'relative',
                                transform: `translate(${previewTemplate.design?.layoutSettings?.titleOffsetX || 0}px, ${previewTemplate.design?.layoutSettings?.titleOffsetY || 0}px)`
                            }}
                        >
                            {previewTemplate.body?.title?.text || 'Certificate of Completion'}
                        </h2>

                        <div
                            style={{
                                fontFamily: previewTemplate.body?.text?.font?.family || 'Arial',
                                fontSize: `${previewTemplate.body?.text?.font?.size || 16}px`,
                                color: previewTemplate.body?.text?.font?.color || '#333333',
                                textAlign: previewTemplate.design?.layoutSettings?.bodyAlign || 'center',
                                maxWidth: `${previewTemplate.design?.layoutSettings?.bodyMaxWidth || 600}px`,
                                margin: `0 auto ${previewTemplate.design?.layoutSettings?.bodySpacing || 30}px`,
                                position: 'relative',
                                transform: `translate(${previewTemplate.design?.layoutSettings?.bodyOffsetX || 0}px, ${previewTemplate.design?.layoutSettings?.bodyOffsetY || 0}px)`,
                                whiteSpace: 'pre-wrap'
                            }}
                        >
                            {(() => {
                                const template = previewTemplate.body?.text?.template || 'This is to certify that {{studentName}} has successfully completed the {{courseName}} course.';
                                const sampleData = {
                                    '{{studentName}}': 'John Doe',
                                    '{{courseName}}': 'Sample Course',
                                    '{{completionDate}}': new Date().toLocaleDateString(),
                                    '{{issueDate}}': previewTemplate.footer?.issueDate || new Date().toLocaleDateString(),
                                    '{{certificateNumber}}': 'CERT-001',
                                    '{{grade}}': 'A+',
                                    '{{duration}}': '3 months',
                                    '{{instructorName}}': 'Jane Smith',
                                    '{{academyName}}': 'Academy Name'
                                };
                                const styles = previewTemplate.placeholderStyles || {};
                                const parts = template.split(/(\{\{[^}]+\}\})/g);

                                return parts.map((part, index) => {
                                    if (part.match(/\{\{[^}]+\}\}/)) {
                                        const style = styles[part] || {};
                                        return (
                                            <span
                                                key={index}
                                                style={{
                                                    fontFamily: style.fontFamily,
                                                    fontSize: style.fontSize ? `${style.fontSize}px` : undefined,
                                                    color: style.color,
                                                    fontWeight: style.fontWeight || 'normal',
                                                    fontStyle: style.fontStyle || 'normal',
                                                    textDecoration: style.textDecoration || 'none',
                                                    textTransform: style.textTransform || 'none'
                                                }}
                                            >
                                                {sampleData[part] || part}
                                            </span>
                                        );
                                    }
                                    return <span key={index}>{part}</span>;
                                });
                            })()}
                        </div>

                        {/* Badges */}
                        {(() => {
                            console.log('Preview modal - Rendering badges:', {
                                hasBadges: !!previewTemplate.badges,
                                badgesCount: previewTemplate.badges?.length || 0,
                                badges: previewTemplate.badges
                            });
                            return previewTemplate.badges?.map((badge, index) => (
                                badge.url && (
                                    <div
                                        key={index}
                                        style={{
                                            position: 'absolute',
                                            left: `${badge.x || 50}%`,
                                            top: `${badge.y || 50}%`,
                                            transform: 'translate(-50%, -50%)',
                                            zIndex: 10
                                        }}
                                    >
                                        <img
                                            src={`${config.baseUrl}${badge.url}`}
                                            alt={`Badge ${index + 1}`}
                                            style={{
                                                width: `${badge.size || 80}px`,
                                                height: `${badge.size || 80}px`,
                                                objectFit: 'contain'
                                            }}
                                            onError={(e) => {
                                                console.error('Badge failed to load in preview:', e.target.src);
                                                e.target.style.display = 'none';
                                            }}
                                            onLoad={() => {
                                                console.log('Badge loaded successfully in preview:', badge.url);
                                            }}
                                        />
                                    </div>
                                )
                            ));
                        })()}

                        {/* Watermark */}
                        {previewTemplate?.watermark?.image && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    opacity: previewTemplate.watermark.opacity || 0.1,
                                    zIndex: 1,
                                    pointerEvents: 'none'
                                }}
                            >
                                <img
                                    src={`${config.baseUrl}${previewTemplate.watermark.image}`}
                                    alt="Watermark"
                                    style={{
                                        width: `${previewTemplate.watermark.size || 300}px`,
                                        height: 'auto',
                                        objectFit: 'contain'
                                    }}
                                />
                            </div>
                        )}

                        {/* Footer - Signature Slots and Date */}
                        <div
                            style={{
                                position: 'relative',
                                transform: `translate(${previewTemplate.design?.layoutSettings?.footerOffsetX || 0}px, ${previewTemplate.design?.layoutSettings?.footerOffsetY || 0}px)`,
                                marginTop: `${previewTemplate.design?.layoutSettings?.footerSpacing || 50}px`
                            }}
                        >
                            {/* Signature Slots */}
                            {previewTemplate.signatureSlots?.length > 0 && (
                                <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-around', maxWidth: `${previewTemplate.design?.layoutSettings?.footerMaxWidth || 600}px`, margin: '0 auto' }}>
                                    {previewTemplate.signatureSlots.map((slot, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                textAlign: 'center',
                                                flex: slot.position === 'center' ? '0 0 auto' : '1',
                                                maxWidth: '200px'
                                            }}
                                        >
                                            <div style={{ borderTop: '1px solid #000', paddingTop: '5px' }}>
                                                {slot.showSignerName && slot.signerName && (
                                                    <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '3px' }}>
                                                        <Text>{slot.signerName}</Text>
                                                    </div>
                                                )}
                                                {slot.showLabel && <Text>{slot.label}</Text>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Date */}
                            {previewTemplate.footer?.showDate && (
                                <div style={{ textAlign: previewTemplate.design?.layoutSettings?.footerAlign || 'center', width: '100%' }}>
                                    <div style={{ fontSize: '14px', marginBottom: '5px' }}>
                                        {previewTemplate.footer?.issueDate || new Date().toLocaleDateString()}
                                    </div>

                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default CertificateTemplates;
